import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import AFIPVerification from '../components/AFIPVerification';
import { CvusUnificadosTabla } from '../components/padron/CvusUnificadosTabla';
import {
  Building2,
  ArrowLeft,
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  User,
  CreditCard,
  AlertTriangle,
  RefreshCw,
  X,
  Plus,
  Trash2,
  Eye,
  MessageSquare,
  Calendar,
  Send,
  Edit2,
  Link2,
  FilePlus,
  History,
  FileWarning,
  AlertCircle,
  UserCheck,
  FileCheck,
  ExternalLink,
  DollarSign,
  TrendingUp,
  BarChart3,
  Percent,
  Save,
  Download,
  PackageOpen,
  Eye as EyeIcon,
  EyeOff,
  Zap,
  Settings,
  Search,
  Activity,
  Shield,
} from 'lucide-react';

const ESTADOS = [
  { value: 'ACTIVA', label: 'Activa', color: 'bg-green-100 text-green-700' },
  { value: 'INACTIVO', label: 'Inactivo', color: 'bg-slate-100 text-slate-700' },
  { value: 'BORRADOR', label: 'Borrador', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'EN_REVISION', label: 'En Revisión', color: 'bg-blue-100 text-blue-700' },
  { value: 'DOCUMENTACION_APROBADA', label: 'Doc. Aprobada', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'APROBADA', label: 'Aprobada', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'RECHAZADA', label: 'Rechazada', color: 'bg-red-100 text-red-700' },
  { value: 'INFO_ADICIONAL', label: 'Info Adicional', color: 'bg-orange-100 text-orange-700' },
];

const DOC_ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  EN_REVISION: { label: 'En Revisión', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  APROBADO: { label: 'Aprobado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  RECHAZADO: { label: 'Rechazado', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const LIMIT_LABELS_MAP = {
  limite_diario: 'Límite Diario',
  limite_mensual: 'Límite Mensual',
  limite_alerta: 'Límite Alerta',
  limite_bloqueo_retiro: 'Límite Bloqueo Retiro',
  limite_bloqueo_cuenta: 'Límite Bloqueo Cuenta',
  limite_bloqueo_cvu: 'Límite Bloqueo CVU',
};

// Etapas del flujo "Continuar proceso" para el stepper en vivo
const ETAPAS_CONT = [
  { key: 'ACTIVANDO', label: 'Activando' },
  { key: 'SUCURSAL', label: 'Sucursal' },
  { key: 'USUARIO', label: 'Usuario' },
  { key: 'MP', label: 'Medios de Pago' },
];

// Índice de etapa actual (0..3, o 4 = completado, -1 = sin progreso) a partir del progreso
const etapaIndex = (prog) => {
  if (!prog) return -1;
  if (prog.etapa === 'COMPLETADO' || prog.completado) return 4;
  if (prog.etapa) {
    const i = ETAPAS_CONT.findIndex(e => e.key === prog.etapa);
    if (i >= 0) return i;
  }
  const t = (prog.paso || '').toLowerCase();
  if (t.includes('complet')) return 4;
  if (/(medio|qr|saldo|billetera|mediopago)/.test(t)) return 3;
  if (/(asign|usuario)/.test(t)) return 2;
  if (t.includes('sucursal')) return 1;
  return 0;
};

// ============== Preview de Documento (carga dinámica) ==============
const DocPreviewContent = ({ doc, empresaId }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoke = null;
    const hasInlineData = doc.url?.startsWith('data:');
    if (hasInlineData) return; // Already has inline data

    setLoading(true);
    setError(false);
    api.get(`/empresas/${empresaId}/documentos/${doc.id}/download`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(res.data);
        revoke = url;
        setBlobUrl(url);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    return () => { if (revoke) window.URL.revokeObjectURL(revoke); };
  }, [doc.id, doc.url, empresaId]);

  const previewUrl = doc.url?.startsWith('data:') ? doc.url : blobUrl;
  const mimeType = doc.content_type || doc.mimetype || '';
  const isImage = mimeType.startsWith('image/') || doc.url?.startsWith('data:image');
  const isPdf = mimeType === 'application/pdf';

  const handleDownload = async () => {
    try {
      const response = await api.get(`/empresas/${empresaId}/documentos/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || doc.nombre || 'documento';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Documento descargado');
    } catch {
      toast.error('Error al descargar documento');
    }
  };

  if (loading) {
    return (
      <div className="text-center space-y-2 py-4">
        <RefreshCw size={24} className="mx-auto animate-spin text-purple-500" />
        <p className="text-sm text-slate-500">Cargando vista previa...</p>
      </div>
    );
  }

  return (
    <div className="text-center space-y-4 w-full">
      {isImage && previewUrl && (
        <img src={previewUrl} alt={doc.nombre} className="max-h-64 mx-auto rounded-lg shadow" />
      )}
      {isPdf && previewUrl && (
        <iframe src={previewUrl} className="w-full h-64 rounded-lg" title={doc.nombre} />
      )}
      {(!previewUrl || (!isImage && !isPdf)) && (
        <>
          <FileText size={48} className="mx-auto mb-2 text-slate-400" />
          <p className="text-sm text-slate-500">{doc.filename || doc.nombre}</p>
          {doc.size > 0 && <p className="text-xs text-slate-400">{(doc.size / 1024).toFixed(1)} KB</p>}
          {error && <p className="text-xs text-red-500">No se pudo cargar la vista previa</p>}
        </>
      )}
      <Button onClick={handleDownload} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="doc-preview-download-btn">
        <Download size={14} className="mr-2" />
        Descargar Documento
      </Button>
    </div>
  );
};

// ============== Panel de Alertas y Casos ==============
const AlertasCasosPanel = ({ empresaId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ alertas: [], casos: [], total_alertas: 0, total_casos: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/empresas/${empresaId}/alertas-casos`);
        setData(res.data);
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [empresaId]);

  const getSeveridadColor = (sev) => {
    const colors = { CRITICA: 'bg-red-100 text-red-700', ALTA: 'bg-orange-100 text-orange-700', MEDIA: 'bg-yellow-100 text-yellow-700', BAJA: 'bg-slate-100 text-slate-700' };
    return colors[sev] || 'bg-slate-100 text-slate-700';
  };

  const getEstadoCasoColor = (estado) => {
    const colors = { ABIERTO: 'bg-blue-100 text-blue-700', EN_INVESTIGACION: 'bg-purple-100 text-purple-700', CERRADO: 'bg-green-100 text-green-700', ARCHIVADO: 'bg-slate-100 text-slate-700' };
    return colors[estado] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <Card className="p-6 bg-white"><p className="text-sm text-slate-400 text-center">Cargando alertas y casos...</p></Card>;
  if (data.total_alertas === 0 && data.total_casos === 0) return null;

  return (
    <Card className="p-6 bg-white" data-testid="alertas-casos-panel">
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
        <AlertTriangle className="text-amber-600" size={20} />
        Alertas y Casos ({data.total_alertas} alertas, {data.total_casos} casos)
      </h2>

      {data.casos.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-slate-600 mb-2">Casos</h3>
          <div className="space-y-2">
            {data.casos.map((caso) => (
              <div
                key={caso.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => navigate(`/m/gestion-de-casos/caso/${caso.id}`)}
                data-testid={`caso-row-${caso.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{caso.titulo || 'Sin título'}</p>
                  <p className="text-xs text-slate-500">{caso.tipo_alerta} | {caso.asignado_a || 'Sin asignar'}</p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <Badge className={getEstadoCasoColor(caso.estado)}>{caso.estado?.replace(/_/g, ' ')}</Badge>
                  <ExternalLink size={14} className="text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.alertas.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Alertas</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {data.alertas.map((alerta) => (
              <div key={alerta.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`alerta-row-${alerta.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{alerta.tipo_alerta}</p>
                  <p className="text-xs text-slate-500">
                    {alerta.cuenta_id?.substring(0, 15)}... | ${alerta.monto ? Number(alerta.monto).toLocaleString('es-AR') : '0'}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <Badge className={getSeveridadColor(alerta.severidad)}>{alerta.severidad}</Badge>
                  <Badge variant="outline" className="text-xs">{alerta.estado}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

// ============== Panel de Comisiones / Rubros ==============
const ComisionPanel = ({ empresa, onUpdate, rubrosDisponibles }) => {
  const [showRubros, setShowRubros] = useState(false);
  const [rubros, setRubros] = useState(rubrosDisponibles || []);
  const [loadingRubros, setLoadingRubros] = useState(false);
  // Propagation modal state
  const [showPropagar, setShowPropagar] = useState(false);
  const [nuevoRubroParaPropagar, setNuevoRubroParaPropagar] = useState(null);
  const [propSeleccion, setPropSeleccion] = useState({});
  const [propProcessing, setPropProcessing] = useState(false);
  const [propBatchId, setPropBatchId] = useState(null);
  const [propProgreso, setPropProgreso] = useState(null);
  // Edit single sucursal rubro
  const [editSucRubro, setEditSucRubro] = useState(null); // {waya_sucursal_id, nombre, rubro_id}

  const loadRubros = async () => {
    setLoadingRubros(true);
    try {
      const res = await api.get('/waya/rubros');
      setRubros(res.data.rubros || []);
    } catch { toast.error('Error al cargar rubros'); }
    finally { setLoadingRubros(false); }
  };

  const handleSelectRubro = async (rubro) => {
    try {
      await api.post(`/waya/empresa/${empresa.id}/set-rubro-default`, {
        rubro_id: rubro.id, rubro_nombre: rubro.nombre, rubro_porcentaje: rubro.porcentaje
      });
      toast.success(`Rubro default: ${rubro.nombre} (${rubro.porcentaje}%)`);
      setShowRubros(false);
      onUpdate();

      // If there are sucursales, ask if they want to propagate the new default
      const sucs = empresa?.sucursales_waya || [];
      if (sucs.length > 0) {
        setNuevoRubroParaPropagar(rubro);
        const sel = {};
        // Pre-select sucursales that have a DIFFERENT rubro
        sucs.forEach(s => {
          if (!s.rubro_id || s.rubro_id !== rubro.id) {
            sel[s.waya_sucursal_id] = true;
          }
        });
        setPropSeleccion(sel);
        setPropBatchId(null);
        setPropProgreso(null);
        setPropProcessing(false);
        setShowPropagar(true);
      }
    } catch { toast.error('Error al configurar rubro'); }
  };

  const handleDeleteRubro = async () => {
    try {
      await api.delete(`/waya/empresa/${empresa.id}/rubro-default`);
      toast.success('Rubro default eliminado');
      onUpdate();
    } catch { toast.error('Error al eliminar rubro'); }
  };

  const handlePropagarRubro = async () => {
    const selIds = Object.entries(propSeleccion).filter(([, v]) => v).map(([id]) => id);
    if (selIds.length === 0) { toast.warning('Seleccioná al menos una sucursal'); return; }
    setPropProcessing(true);
    try {
      const sucs = (empresa.sucursales_waya || []).filter(s => selIds.includes(s.waya_sucursal_id));
      const res = await api.post('/waya/modificar-rubro-batch', {
        empresa_id: empresa.id,
        nuevo_rubro_id: nuevoRubroParaPropagar.id,
        nuevo_rubro_nombre: nuevoRubroParaPropagar.nombre,
        sucursales: sucs.map(s => ({ waya_sucursal_id: s.waya_sucursal_id, sucursal_nombre: s.nombre })),
      });
      const bId = res.data.batch_id;
      setPropBatchId(bId);
      const poll = setInterval(async () => {
        try {
          const pRes = await api.get(`/waya/batch-rubro/${bId}/progreso`, { skipAuthRedirect: true });
          setPropProgreso(pRes.data);
          if (pRes.data.estado === 'COMPLETADO') {
            clearInterval(poll);
            setPropProcessing(false);
            toast.success(`Rubro actualizado en ${pRes.data.completadas} sucursales`);
            onUpdate();
          }
        } catch { /* ignore */ }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setPropProcessing(false); }, 600000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al propagar rubro');
      setPropProcessing(false);
    }
  };

  const handleEditSucRubro = async (sucursal, nuevoRubroId) => {
    const rubro = rubros.find(r => r.id === nuevoRubroId);
    if (!rubro) return;
    try {
      toast.info(`Cambiando rubro de ${sucursal.nombre}...`);
      const res = await api.post('/waya/modificar-rubro-sucursal', {
        empresa_id: empresa.id,
        waya_sucursal_id: sucursal.waya_sucursal_id,
        nuevo_rubro_id: nuevoRubroId,
        nuevo_rubro_nombre: rubro.nombre,
      });
      if (res.data.job_id) {
        // Poll for result
        const poll = setInterval(async () => {
          try {
            const jRes = await api.get(`/waya/job/${res.data.job_id}`);
            if (jRes.data.estado === 'COMPLETADO') {
              clearInterval(poll);
              toast.success(jRes.data.mensaje || 'Rubro actualizado');
              setEditSucRubro(null);
              onUpdate();
            } else if (jRes.data.estado === 'ERROR') {
              clearInterval(poll);
              toast.error(jRes.data.mensaje || 'Error al cambiar rubro');
            }
          } catch { /* ignore */ }
        }, 5000);
        setTimeout(() => clearInterval(poll), 120000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const rubroDefault = empresa?.rubro_default;
  const sucursales = empresa?.sucursales_waya || [];

  return (
    <Card className="p-6 bg-white" data-testid="comision-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Percent className="text-green-600" size={20} />
          Comisiones
        </h2>
        <Button variant="ghost" size="sm" onClick={() => { setShowRubros(!showRubros); if (!rubros.length) loadRubros(); }} data-testid="btn-rubros-toggle" title="Seleccionar rubro">
          {showRubros ? <X size={16} /> : <Edit2 size={16} />}
        </Button>
      </div>

      {rubroDefault && !showRubros && (
        <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200" data-testid="rubro-default">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[10px] text-green-600 uppercase font-medium">Rubro Default</p>
              <p className="text-sm font-semibold text-green-800">{rubroDefault.nombre}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-green-700">{rubroDefault.porcentaje}%</span>
              <button onClick={handleDeleteRubro} className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors" title="Eliminar rubro default" data-testid="btn-delete-rubro">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sucursales con su comisión */}
      {sucursales.length > 0 && !showRubros && (
        <div className="space-y-1.5 mb-3">
          <p className="text-[10px] text-slate-500 uppercase font-medium">Comisión por sucursal</p>
          {sucursales.map((suc, idx) => {
            const isEditing = editSucRubro?.waya_sucursal_id === suc.waya_sucursal_id;
            const isDifferent = suc.rubro_id && suc.rubro_id !== rubroDefault?.id;
            return (
              <div key={idx} className={`p-2 rounded border ${isDifferent ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex justify-between items-center text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[10px] text-slate-400">#{suc.waya_sucursal_id}</span>
                    <span className="font-medium text-slate-700">{suc.nombre}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isEditing ? (
                      <select
                        className="text-xs border rounded px-1.5 py-0.5 bg-white"
                        defaultValue={suc.rubro_id || ''}
                        onChange={(e) => { if (e.target.value && e.target.value !== suc.rubro_id) handleEditSucRubro(suc, e.target.value); }}
                        data-testid={`edit-rubro-select-${idx}`}
                      >
                        <option value="">Seleccionar...</option>
                        {rubros.map(r => (
                          <option key={r.id} value={r.id}>{r.nombre} ({r.porcentaje}%)</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`font-semibold ${isDifferent ? 'text-amber-700' : 'text-slate-600'}`}>
                        {suc.rubro_nombre || 'Sin rubro'}
                      </span>
                    )}
                    <button
                      onClick={() => { if (!rubros.length) loadRubros(); setEditSucRubro(isEditing ? null : suc); }}
                      className="p-0.5 rounded hover:bg-blue-100 text-blue-400 hover:text-blue-600 transition-colors"
                      title="Editar comisión" data-testid={`edit-rubro-btn-${idx}`}
                    >
                      {isEditing ? <X size={12} /> : <Edit2 size={12} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showRubros && (
        <div className="mb-4 max-h-[300px] overflow-y-auto border rounded-lg" data-testid="rubros-list">
          <div className="sticky top-0 bg-slate-100 px-3 py-1.5 text-[10px] text-slate-500 uppercase font-medium border-b">
            Seleccionar rubro como default
          </div>
          {loadingRubros ? (
            <div className="text-center py-4"><RefreshCw size={16} className="animate-spin mx-auto" /></div>
          ) : rubros.map(r => (
            <button key={r.id} onClick={() => handleSelectRubro(r)}
              className={`w-full text-left px-3 py-2 hover:bg-green-50 flex justify-between items-center text-sm border-b last:border-b-0 ${rubroDefault?.id === r.id ? 'bg-green-100 font-semibold' : ''}`}
              data-testid={`rubro-option-${r.id}`}
            >
              <span>{r.nombre}</span>
              <span className="text-slate-500 font-mono text-xs">{r.porcentaje}%</span>
            </button>
          ))}
        </div>
      )}

      {!rubroDefault && !showRubros && sucursales.length === 0 && (
        <div className="text-center py-3">
          <Percent size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">Sin comisión configurada</p>
          <p className="text-xs text-slate-400 mt-1">Use el lápiz para seleccionar un rubro</p>
        </div>
      )}

      {/* Modal: Propagar rubro a sucursales existentes */}
      <Dialog open={showPropagar} onOpenChange={(o) => { if (!propProcessing) setShowPropagar(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aplicar nuevo rubro a sucursales</DialogTitle>
            <DialogDescription>
              El rubro default cambió a <strong>{nuevoRubroParaPropagar?.nombre}</strong> ({nuevoRubroParaPropagar?.porcentaje}%).
              Seleccioná las sucursales que querés actualizar en Waya:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            <div className="flex gap-2 mb-2">
              <Button size="sm" variant="outline" className="text-xs h-7"
                onClick={() => { const sel = {}; sucursales.filter(s => s.rubro_id !== nuevoRubroParaPropagar?.id).forEach(s => { sel[s.waya_sucursal_id] = true; }); setPropSeleccion(sel); }}>
                Todas las diferentes
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7"
                onClick={() => { const sel = {}; sucursales.forEach(s => { sel[s.waya_sucursal_id] = true; }); setPropSeleccion(sel); }}>
                Todas
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setPropSeleccion({})}>
                Ninguna
              </Button>
            </div>
            {sucursales.map((suc, idx) => {
              const itemProg = propProgreso?.items?.find(i => i.waya_sucursal_id === suc.waya_sucursal_id);
              const yaIgual = suc.rubro_id === nuevoRubroParaPropagar?.id;
              return (
                <label key={idx} className={`flex items-center justify-between p-2 rounded border cursor-pointer hover:bg-slate-50 ${yaIgual ? 'border-green-200 bg-green-50 opacity-70' : propSeleccion[suc.waya_sucursal_id] ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!propSeleccion[suc.waya_sucursal_id]}
                      onChange={(e) => setPropSeleccion({ ...propSeleccion, [suc.waya_sucursal_id]: e.target.checked })}
                      disabled={propProcessing || yaIgual} className="rounded" />
                    <div>
                      <span className="text-sm font-medium">{suc.nombre}</span>
                      <span className="text-xs text-slate-400 ml-2">#{suc.waya_sucursal_id}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    {itemProg ? (
                      <Badge className={itemProg.estado === 'COMPLETADA' ? 'bg-green-100 text-green-700' : itemProg.estado === 'ERROR' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                        {itemProg.progreso || itemProg.estado}
                      </Badge>
                    ) : yaIgual ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Ya tiene este rubro</Badge>
                    ) : (
                      <span className="text-xs text-amber-600 font-medium">{suc.rubro_nombre || 'Sin rubro'}</span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          {propProgreso && (
            <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-center">
              Completadas: {propProgreso.completadas}/{propProgreso.total} | Errores: {propProgreso.errores}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPropagar(false)} disabled={propProcessing}>
              {propProgreso?.estado === 'COMPLETADO' ? 'Cerrar' : 'No aplicar'}
            </Button>
            {(!propProgreso || propProgreso.estado !== 'COMPLETADO') && (
              <Button onClick={handlePropagarRubro} disabled={propProcessing || Object.values(propSeleccion).filter(Boolean).length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
                {propProcessing ? <><RefreshCw size={14} className="animate-spin mr-1" /> Procesando...</> : `Aplicar a ${Object.values(propSeleccion).filter(Boolean).length} sucursales`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============== Panel de Estadísticas ==============
const EstadisticasPanel = ({ empresaId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [empresaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/empresas/${empresaId}/estadisticas/resumen`);
      setStats(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(value);
  };

  return (
    <Card className="p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="text-blue-600" size={20} />
          Estadísticas
        </h2>
        <Button variant="ghost" size="sm" onClick={loadStats}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-4">
          <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-400 mt-2">Cargando...</p>
        </div>
      ) : stats ? (
        <div className="space-y-4">
          <p className="text-xs text-slate-500 mb-3">Últimos 30 días</p>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <TrendingUp size={16} className="mx-auto text-green-600 mb-1" />
              <p className="text-xs text-green-600">Entradas</p>
              <p className="font-bold text-green-800 text-sm">{formatMoney(stats.resumen?.entradas_30_dias)}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <TrendingUp size={16} className="mx-auto text-red-600 mb-1 rotate-180" />
              <p className="text-xs text-red-600">Salidas</p>
              <p className="font-bold text-red-800 text-sm">{formatMoney(stats.resumen?.salidas_30_dias)}</p>
            </div>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700">Volumen Total</span>
              <span className="font-bold text-blue-800">{formatMoney(stats.resumen?.volumen_30_dias)}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-blue-700">Transacciones</span>
              <span className="font-bold text-blue-800">{stats.resumen?.transacciones_30_dias || 0}</span>
            </div>
          </div>
          
          {stats.comision?.activa && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={16} className="text-amber-600" />
                <span className="font-medium text-amber-800">Comisión Generada</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">{formatMoney(stats.comision?.acumulada)}</p>
              <p className="text-xs text-amber-600 mt-1">({stats.comision?.porcentaje}% sobre entradas)</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4">
          <BarChart3 size={32} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">Sin datos de movimientos</p>
        </div>
      )}
    </Card>
  );
};

// A3: entrando por id de empresa o padron-<CUIT> se redirige a la ficha única del CUIT.
// Con `embebido` (desde la ficha de una persona jurídica) se muestra completa, sin redirigir.
const EmpresaDetalle = ({ empresaIdProp, embebido = false }) => {
  const { empresaId: empresaIdRuta } = useParams();
  const empresaId = empresaIdProp || empresaIdRuta;
  const navigate = useNavigate();
  const irAFicha = (cuit) => {
    const mod = window.location.pathname.split('/')[2] || 'clientes';
    navigate(`/m/${mod}/cliente/${String(cuit || '').replace(/\D/g, '')}`, { replace: true });
  };
  
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCvuDialog, setShowCvuDialog] = useState(false);
  const [showRepDialog, setShowRepDialog] = useState(false);
  const [showVincularDialog, setShowVincularDialog] = useState(false);
  const [showDocPreview, setShowDocPreview] = useState(null);
  const [showEstadoDialog, setShowEstadoDialog] = useState(false);
  
  // Upload
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  
  // Forms
  const [newCvu, setNewCvu] = useState({ cvu: '', alias: '', banco: '' });
  const [repData, setRepData] = useState({ cuit: '', es_excepcion: false, justificacion: '' });
  const [searchingRep, setSearchingRep] = useState(false);
  const [vincularCuit, setVincularCuit] = useState('');
  
  // Estado change
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [motivoEstado, setMotivoEstado] = useState('');
  const [asignadoA, setAsignadoA] = useState('');
  
  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  
  // Crear Cuentas Waya
  const [showCrearCuentasModal, setShowCrearCuentasModal] = useState(false);
  const [limitesCuenta, setLimitesCuenta] = useState({
    limite_diario: '1000000.00',
    limite_mensual: '500000000.00',
    limite_alerta: '20000000.00',
    limite_bloqueo_retiro: '25000000.00',
    limite_bloqueo_cuenta: '20000000.00',
    limite_bloqueo_cvu: '23000000.00',
  });
  const [aprobarIdentidad, setAprobarIdentidad] = useState(true);
  const [numCuentas, setNumCuentas] = useState(1);
  const [datosCompartidos, setDatosCompartidos] = useState({
    cuit: '', actividad: '', inicio_actividad: '', celular: '',
    pep_uif: 'NO', password: '', pin: ''
  });
  const [cuentasIndividuales, setCuentasIndividuales] = useState([]);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [creandoCuentas, setCreandoCuentas] = useState(false);
  
  // Historial de cuentas
  const [cuentasHistorial, setCuentasHistorial] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);

  // Modal Modificar Límites
  const [showModLimitesModal, setShowModLimitesModal] = useState(false);
  const [modLimitesCuenta, setModLimitesCuenta] = useState(null);
  const [modLimites, setModLimites] = useState({
    limite_diario: '',
    limite_mensual: '',
    limite_alerta: '',
    limite_bloqueo_retiro: '',
    limite_bloqueo_cuenta: '',
    limite_bloqueo_cvu: '',
  });
  const [modificandoLimites, setModificandoLimites] = useState(false);

  // Retomar activación (cuentas trabadas en PENDIENTE)
  const [showRetomarModal, setShowRetomarModal] = useState(false);
  const [retomarCuenta, setRetomarCuenta] = useState(null);
  const [retomarLimites, setRetomarLimites] = useState({
    limite_diario: '', limite_mensual: '', limite_alerta: '',
    limite_bloqueo_retiro: '', limite_bloqueo_cuenta: '', limite_bloqueo_cvu: '',
  });
  const [retomarAprobar, setRetomarAprobar] = useState(true);
  const [retomando, setRetomando] = useState(false);

  // Continuar proceso (resume completo: activación + sucursal en etapas) — para cuentas SINCRONIZADAS o trabadas
  const [continuarModal, setContinuarModal] = useState(null); // { cu, limites, aprobar, crearSucursal }
  const [continuando, setContinuando] = useState(false);
  const [continuarProgresoMap, setContinuarProgresoMap] = useState({}); // email -> progreso (etapas en vivo)
  const continuarPollRef = useRef({});

  // Progreso de activación/modificación
  const [progresoMap, setProgresoMap] = useState({});

  // Vincular cuenta externa
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [vincularEmail, setVincularEmail] = useState('');
  const [vinculando, setVinculando] = useState(false);

  // Setup sucursal por pasos (granular)
  const [stepsModal, setStepsModal] = useState(null); // { cuenta, existingSuc, steps: {crear, usuario, medios}, sucursalId }
  const [stepsRunning, setStepsRunning] = useState(false);

  // Padrón resumen
  const [padronResumen, setPadronResumen] = useState(null);
  const [loadingPadron, setLoadingPadron] = useState(false);

  // Waya Empresa/Sucursal
  const [showWayaModal, setShowWayaModal] = useState(false);
  const [wayaMode, setWayaMode] = useState(''); // 'crear' | 'vincular'
  const [wayaNombre, setWayaNombre] = useState('');
  const [wayaId, setWayaId] = useState('');
  const [wayaLoading, setWayaLoading] = useState(false);
  const [wayaRefrescando, setWayaRefrescando] = useState(false);
  const [rubrosDisponibles, setRubrosDisponibles] = useState([]);

  // Batch Sucursales
  const [showBatchSucModal, setShowBatchSucModal] = useState(false);
  const [batchSucSeleccion, setBatchSucSeleccion] = useState({});
  const [batchSucRubros, setBatchSucRubros] = useState({});
  const [batchSucProcessing, setBatchSucProcessing] = useState(false);
  const [batchSucBatchId, setBatchSucBatchId] = useState(null);
  const [batchSucProgreso, setBatchSucProgreso] = useState(null);
  const batchSucPollRef = useRef(null);

  // Batch Medios de Pago
  const [showBatchMPModal, setShowBatchMPModal] = useState(false);
  const [batchMPSeleccion, setBatchMPSeleccion] = useState({});
  const [batchMPProcessing, setBatchMPProcessing] = useState(false);
  const [batchMPBatchId, setBatchMPBatchId] = useState(null);
  const [batchMPProgreso, setBatchMPProgreso] = useState(null);

  useEffect(() => {
    if (!embebido && String(empresaId || '').startsWith('padron-')) {
      irAFicha(empresaId.slice('padron-'.length));
      return;
    }
    loadEmpresa();
    loadTiposDocumento();
    loadUsers();
  }, [empresaId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Limpiar intervalos de "Continuar proceso" al desmontar
  useEffect(() => {
    const refs = continuarPollRef.current;
    return () => { Object.values(refs).forEach(iv => clearInterval(iv)); };
  }, []);


  // Cargar cuentas cuando la empresa esté disponible
  useEffect(() => {
    if (empresa?.id) {
      loadCuentasHistorial();
      loadPadronResumen();
      api.get('/waya/rubros').then(r => setRubrosDisponibles(r.data.rubros || [])).catch(() => {});
    }
  }, [empresa?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmpresa = async () => {
    const isInitial = !empresa;
    if (isInitial) setLoading(true);
    try {
      // skipAuthRedirect: un 401 transitorio durante un proceso Waya largo no debe mandar a /login
      const res = await api.get(`/empresas/${empresaId}`, { skipAuthRedirect: true });
      if (isInitial && !embebido && res.data?.cuit) {
        irAFicha(res.data.cuit);
        return;
      }
      setEmpresa(res.data);
    } catch (error) {
      if (isInitial) {
        // Sólo en la carga inicial fallida volvemos atrás
        toast.error('Error al cargar empresa');
        navigate(-1);
      } else {
        // Refresh transitorio (ej. backend reiniciando durante Playwright): NO sacamos al usuario
        // de la página (evita pantalla en blanco). Mantenemos los datos actuales.
        toast.warning('No se pudo refrescar la empresa. Reintentá en unos segundos.');
      }
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const pollWayaJob = (jobId, label) => {
    let attempts = 0;
    const maxAttempts = 60;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await api.get(`/waya/job/${jobId}`, { skipAuthRedirect: true });
        const job = res.data;
        if (job.estado === 'COMPLETADO') {
          clearInterval(interval);
          toast.success(job.mensaje || `${label}: Completado`);
          loadEmpresa();
        } else if (job.estado === 'ERROR') {
          clearInterval(interval);
          toast.error(job.mensaje || `${label}: Error`);
          loadEmpresa();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          toast.warning(`${label}: Timeout, recargá para ver el estado`);
          loadEmpresa();
        }
      } catch {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          loadEmpresa();
        }
      }
    }, 5000);
  };

  const loadTiposDocumento = async () => {
    try {
      const res = await api.get('/empresas/config/tipos-documento');
      setTiposDocumento(res.data);
    } catch (error) {
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (error) {
    }
  };

  const loadPadronResumen = async () => {
    if (!empresa?.id || empresa.id.startsWith('padron-')) return;
    setLoadingPadron(true);
    try {
      const res = await api.get(`/empresas/${empresa.id}/padron-resumen`);
      setPadronResumen(res.data);
    } catch (error) {
      // No padron data available
    } finally {
      setLoadingPadron(false);
    }
  };

  const getEstadoStyle = (estado) => {
    return ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  };

  // ============== Análisis de Estado ==============
  const getStatusAnalysis = () => {
    if (!empresa) return { status: 'loading', message: '', color: 'slate' };
    
    const docsRequeridos = tiposDocumento.filter(t => t.obligatorio);
    const docsSubidos = empresa.documentos || [];
    const docsFaltantes = docsRequeridos.filter(tipo => 
      !docsSubidos.some(d => d.tipo?.toUpperCase() === tipo.codigo?.toUpperCase())
    );
    const docsRechazados = docsSubidos.filter(d => d.estado === 'RECHAZADO');
    const docsPendientes = docsSubidos.filter(d => d.estado === 'PENDIENTE');
    const docsAprobados = docsSubidos.filter(d => d.estado === 'APROBADO');
    
    const tieneRepLegal = !!empresa.representante_legal;
    const tieneCuentas = (empresa.cuentas_vinculadas?.length || 0) > 0;
    
    // Análisis por prioridad
    if (empresa.estado === 'APROBADA') {
      return {
        status: 'approved',
        message: 'Empresa aprobada y activa',
        color: 'green',
        icon: CheckCircle
      };
    }
    
    if (empresa.estado === 'RECHAZADA') {
      return {
        status: 'rejected',
        message: `Empresa rechazada: ${empresa.motivo_rechazo || 'Sin motivo'}`,
        color: 'red',
        icon: XCircle
      };
    }
    
    if (empresa.estado === 'EN_REVISION') {
      return {
        status: 'review',
        message: empresa.en_revision_por 
          ? `EN REVISIÓN POR: ${empresa.en_revision_por}`
          : 'En proceso de revisión',
        color: 'blue',
        icon: RefreshCw,
        reviewer: empresa.en_revision_por
      };
    }
    
    if (docsRechazados.length > 0) {
      return {
        status: 'docs_rejected',
        message: `${docsRechazados.length} documento(s) rechazado(s) - Requiere corrección`,
        color: 'red',
        icon: FileWarning
      };
    }
    
    if (docsFaltantes.length > 0) {
      return {
        status: 'docs_missing',
        message: `Falta documentación: ${docsFaltantes.length} documento(s) requerido(s)`,
        color: 'red',
        icon: FileWarning,
        details: docsFaltantes.map(d => d.nombre)
      };
    }
    
    if (!tieneRepLegal) {
      return {
        status: 'rep_missing',
        message: 'Falta asignar Representante Legal',
        color: 'orange',
        icon: User
      };
    }
    
    if (!tieneCuentas) {
      return {
        status: 'account_missing',
        message: 'Falta cuenta titular asociada',
        color: 'orange',
        icon: Link2
      };
    }
    
    if (docsPendientes.length > 0) {
      return {
        status: 'docs_pending',
        message: `${docsPendientes.length} documento(s) pendiente(s) de revisión`,
        color: 'yellow',
        icon: Clock
      };
    }
    
    // Todo completo
    const allDocsApproved = docsRequeridos.every(tipo =>
      docsSubidos.some(d => d.tipo?.toUpperCase() === tipo.codigo?.toUpperCase() && d.estado === 'APROBADO')
    );
    
    if (allDocsApproved && tieneRepLegal && tieneCuentas) {
      return {
        status: 'ready',
        message: 'Lista para aprobar - Toda la documentación completa',
        color: 'green',
        icon: FileCheck
      };
    }
    
    return {
      status: 'incomplete',
      message: 'Completar información requerida',
      color: 'slate',
      icon: AlertCircle
    };
  };

  // Safe date formatting helper
  const formatDocDate = (dateStr, style = 'date') => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return style === 'full' ? d.toLocaleString('es-ES') : d.toLocaleDateString('es-ES');
    } catch { return ''; }
  };

  // Get document status for a specific type (case-insensitive)
  const getDocumentoForTipo = (tipoCodigo) => {
    const code = tipoCodigo?.toUpperCase();
    return empresa?.documentos?.find(d => d.tipo?.toUpperCase() === code);
  };

  // ============== Estado ==============
  const openEstadoDialog = (estado) => {
    setNuevoEstado(estado);
    setMotivoEstado('');
    setAsignadoA('');
    setShowEstadoDialog(true);
  };

  const handleChangeEstado = async () => {
    try {
      const payload = {
        estado: nuevoEstado,
        motivo: motivoEstado || null,
        asignado_a: nuevoEstado === 'EN_REVISION' ? asignadoA : null
      };
      
      const res = await api.patch(`/empresas/${empresaId}/estado`, payload);
      
      // Verificar si requirió aprobación
      if (res.data._approval_required) {
        toast.info(
          `${res.data._approval_message}. Se creó una solicitud de aprobación.`,
          { duration: 6000 }
        );
      } else {
        toast.success('Estado actualizado');
      }
      
      setEmpresa(res.data);
      setShowEstadoDialog(false);
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  // Verificar si un cambio de estado requiere aprobación
  const checkEstadoApproval = async (newEstado) => {
    try {
      const res = await api.get(`/empresas/${empresaId}/check-transition/${newEstado}`);
      return res.data;
    } catch (error) {
      console.error('Error checking transition:', error);
      return { requiere_aprobacion: false };
    }
  };

  // ============== Documentos ==============
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFilesToUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFilesToUpload(files);
  };

  const addFilesToUpload = (files) => {
    const newFiles = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      nombre: file.name,
      tipo: '',
      comentario: '',
      uploading: false,
      uploaded: false,
      error: null
    }));
    setUploadingFiles(prev => [...prev, ...newFiles]);
  };

  const removeFileFromUpload = (fileId) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const updateFileData = (fileId, field, value) => {
    setUploadingFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, [field]: value } : f
    ));
  };

  const uploadDocuments = async () => {
    const filesToUpload = uploadingFiles.filter(f => !f.uploaded && f.tipo);
    
    for (const fileData of filesToUpload) {
      updateFileData(fileData.id, 'uploading', true);
      
      try {
        // Convertir archivo a base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(fileData.file);
        });
        
        await api.post(`/empresas/${empresaId}/documentos`, {
          nombre: fileData.nombre,
          tipo: fileData.tipo,
          url: base64, // Guardar el archivo en base64
          comentario: fileData.comentario,
          filename: fileData.file.name,
          mimetype: fileData.file.type,
          size: fileData.file.size
        });
        
        updateFileData(fileData.id, 'uploaded', true);
        updateFileData(fileData.id, 'uploading', false);
      } catch (error) {
        console.error('Error uploading document:', error);
        updateFileData(fileData.id, 'error', 'Error al subir');
        updateFileData(fileData.id, 'uploading', false);
      }
    }
    
    await loadEmpresa();
    toast.success('Documentos subidos');
    setShowUploadDialog(false);
    setUploadingFiles([]);
  };

  const handleAprobarDocumento = async (docId, aprobar) => {
    let motivo = null;
    if (!aprobar) {
      motivo = prompt('Motivo del rechazo:');
      if (!motivo) return;
    }
    
    try {
      const res = await api.patch(`/empresas/${empresaId}/documentos/${docId}/aprobar`, {
        aprobado: aprobar,
        motivo
      });
      setEmpresa(res.data);
      toast.success(aprobar ? 'Documento aprobado' : 'Documento rechazado');
    } catch (error) {
      toast.error('Error al procesar documento');
    }
  };

  const handleDeleteDocumento = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    
    try {
      const res = await api.delete(`/empresas/${empresaId}/documentos/${docId}`);
      setEmpresa(res.data);
      toast.success('Documento eliminado');
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  // ============== CVU ==============
  const handleAddCvu = async () => {
    if (!newCvu.cvu.trim()) return;
    
    try {
      const res = await api.post(`/empresas/${empresaId}/cvus`, newCvu);
      setEmpresa(res.data);
      setShowCvuDialog(false);
      setNewCvu({ cvu: '', alias: '', banco: '' });
      toast.success('CVU agregado');
    } catch (error) {
      toast.error('Error al agregar CVU');
    }
  };

  const handleDeleteCvu = async (cvuId) => {
    try {
      const res = await api.delete(`/empresas/${empresaId}/cvus/${cvuId}`);
      setEmpresa(res.data);
      toast.success('CVU desactivado');
    } catch (error) {
      toast.error('Error al desactivar CVU');
    }
  };

  // ============== Representante Legal ==============
  const handleSetRepresentante = async () => {
    if (!repData.cuit.trim()) return;
    
    setSearchingRep(true);
    try {
      const res = await api.put(`/empresas/${empresaId}/representante-legal`, {
        cuit: repData.cuit,
        es_excepcion: repData.es_excepcion,
        justificacion_excepcion: repData.justificacion
      });
      setEmpresa(res.data);
      setShowRepDialog(false);
      setRepData({ cuit: '', es_excepcion: false, justificacion: '' });
      toast.success('Representante legal establecido');
    } catch (error) {
      toast.error('Error al establecer representante');
    } finally {
      setSearchingRep(false);
    }
  };

  // ============== Vincular Cuenta ==============
  const handleVincularCuenta = async () => {
    if (!vincularCuit.trim()) return;
    
    try {
      const res = await api.post(`/empresas/${empresaId}/cuentas`, {
        cuit: vincularCuit
      });
      setEmpresa(res.data);
      setShowVincularDialog(false);
      setVincularCuit('');
      toast.success('Cuenta vinculada');
    } catch (error) {
      toast.error('Error al vincular cuenta');
    }
  };

  // === Crear/Vincular Empresa en Waya ===
  const handleCrearEmpresaWaya = async () => {
    if (!wayaNombre.trim()) { toast.error('Ingrese el nombre'); return; }
    setWayaLoading(true);
    try {
      const res = await api.post('/waya/crear-empresa', {
        empresa_id: empresa.id,
        nombre_fantasia: wayaNombre.trim(),
        razon_social: wayaNombre.trim(),
      });
      if (res.data.success) {
        toast.success(`Empresa creada en Waya (ID: ${res.data.waya_id})`);
        setShowWayaModal(false);
        loadEmpresa();
      } else {
        toast.error(res.data.error || 'Error al crear empresa');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 503 || detail.includes('Playwright')) {
        toast.error('Automatización no disponible en este entorno. Use el entorno de preview para crear empresas en Waya.');
      } else {
        toast.error(detail || 'Error al crear empresa en Waya');
      }
    } finally { setWayaLoading(false); }
  };

  const handleVincularEmpresaWaya = async () => {
    if (!wayaId.trim()) { toast.error('Ingrese el ID de Waya'); return; }
    setWayaLoading(true);
    try {
      await api.post('/waya/vincular-empresa', {
        empresa_id: empresa.id,
        waya_id: wayaId.trim(),
      });
      toast.success('Empresa vinculada. Sincronizando sucursales en segundo plano...');
      setShowWayaModal(false);
      loadEmpresa();
      // Poll for sync completion
      setTimeout(() => loadEmpresa(), 10000);
      setTimeout(() => loadEmpresa(), 25000);
      setTimeout(() => loadEmpresa(), 45000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular');
    } finally { setWayaLoading(false); }
  };

  const handleCrearSucursalCuenta = async (cuenta) => {
    if (!cuenta) {
      toast.error('No se pudo abrir el configurador: la cuenta no tiene datos.');
      return;
    }
    const nombre = (cuenta.razon_social || cuenta.email || cuenta.cuit || 'Sucursal sin nombre').toString();
    const sucursalesWaya = empresa.sucursales_waya || [];

    // Check if a sucursal with same name already exists (unlinked)
    const matchName = nombre.trim().toUpperCase();
    const existingSuc = sucursalesWaya.find(s => {
      const sName = (s.nombre || '').trim().toUpperCase();
      if (!sName || !matchName) return false;
      return sName === matchName || sName.includes(matchName) || matchName.includes(sName);
    });

    // Smart defaults: si ya existe la sucursal, no la recreamos; solo asignar usuario + medios de pago.
    const tieneUsuario = existingSuc && (existingSuc.usuarios || []).some(u => u.email);
    setStepsModal({
      cuenta,
      nombre,
      existingSuc: existingSuc || null,
      sucursalId: existingSuc?.waya_sucursal_id || '',
      steps: {
        crear: !existingSuc,            // crear solo si no existe
        usuario: !tieneUsuario,         // asignar usuario si no tiene
        medios: true,                   // medios de pago por defecto
      },
    });
  };

  // Ejecuta los pasos seleccionados en el modal granular
  const handleEjecutarPasos = async () => {
    if (!stepsModal) return;
    const { cuenta, nombre, existingSuc, steps } = stepsModal;
    if (!steps.crear && !steps.usuario && !steps.medios) {
      toast.warning('Seleccioná al menos un paso');
      return;
    }
    if ((steps.usuario || steps.medios) && !steps.crear && !existingSuc?.waya_sucursal_id) {
      toast.error('Para asignar usuario o medios de pago necesitás una sucursal existente o tildar "Crear sucursal".');
      return;
    }
    setStepsRunning(true);
    try {
      toast.info(`Ejecutando pasos para ${nombre}...`);
      const res = await api.post('/waya/setup-sucursal', {
        empresa_id: empresa.id,
        email: cuenta.email,
        nombre_sucursal: existingSuc?.nombre || cuenta.razon_social || (cuenta.email ? cuenta.email.split('@')[0] : nombre),
        codigo_sucursal: existingSuc?.codigo || '',
        rubro_id: existingSuc?.rubro_id || empresa.rubro_default?.id || '',
        existing_sucursal_id: existingSuc?.waya_sucursal_id || null,
        skip_create: !steps.crear,
        skip_assign_user: !steps.usuario,
        skip_medios_pago: !steps.medios,
      }, { skipAuthRedirect: true });
      if (res.data.job_id) {
        pollWayaJob(res.data.job_id, `Sucursal ${nombre}`);
      }
      setStepsModal(null);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 401) {
        toast.error('Tu sesión expiró momentáneamente. Reintentá ejecutar los pasos (no recargues).');
      } else if (error.response?.status === 503 || detail.includes('Playwright')) {
        toast.error('Automatización no disponible en este entorno.');
      } else {
        toast.error(detail || 'Error al ejecutar pasos');
      }
    } finally {
      setStepsRunning(false);
    }
  };

  // ============== Batch Sucursales ==============
  const initBatchSucursales = () => {
    const sinSucursal = (empresa.cuentas_vinculadas || []).filter(c => !c.waya_sucursal_id && c.email);
    if (sinSucursal.length === 0) {
      toast.info('Todas las cuentas ya tienen sucursal asignada');
      return;
    }
    const sel = {};
    const rubs = {};
    const defaultRubro = empresa.rubro_default?.id || '';
    sinSucursal.forEach(c => {
      sel[c.email] = true;
      rubs[c.email] = defaultRubro;
    });
    setBatchSucSeleccion(sel);
    setBatchSucRubros(rubs);
    setBatchSucBatchId(null);
    setBatchSucProgreso(null);
    setBatchSucProcessing(false);
    setShowBatchSucModal(true);
  };

  const handleBatchSucursales = async () => {
    const seleccionadas = Object.entries(batchSucSeleccion).filter(([, v]) => v).map(([email]) => email);
    if (seleccionadas.length === 0) {
      toast.error('Seleccione al menos una cuenta');
      return;
    }
    setBatchSucProcessing(true);
    try {
      const cuentas = seleccionadas.map(email => {
        const cv = (empresa.cuentas_vinculadas || []).find(c => c.email === email);
        return {
          email,
          nombre_sucursal: cv?.razon_social || email.split('@')[0],
          rubro_id: batchSucRubros[email] || empresa.rubro_default?.id || '',
        };
      });
      const res = await api.post('/waya/setup-sucursales-batch', {
        empresa_id: empresa.id,
        cuentas,
      }, { skipAuthRedirect: true });
      const bId = res.data.batch_id;
      setBatchSucBatchId(bId);
      toast.success(`Procesando ${cuentas.length} sucursales...`);
      // Start polling
      if (batchSucPollRef.current) clearInterval(batchSucPollRef.current);
      const poll = setInterval(async () => {
        try {
          const pRes = await api.get(`/waya/batch-sucursales/${bId}/progreso`, { skipAuthRedirect: true });
          setBatchSucProgreso(pRes.data);
          if (pRes.data.estado === 'COMPLETADO' || pRes.data.estado === 'CANCELADO' || pRes.data.estado === 'ERROR') {
            clearInterval(poll);
            batchSucPollRef.current = null;
            setBatchSucProcessing(false);
            loadEmpresa();
            if (pRes.data.estado === 'CANCELADO') {
              toast.info('Proceso cancelado');
            } else if (pRes.data.interrumpido) {
              toast.error('El proceso se interrumpió (reinicio del servidor). Revisá el estado y reintentá las que fallaron.');
            } else {
              toast.success(`Batch completado: ${pRes.data.completadas} OK, ${pRes.data.errores} errores`);
            }
          }
        } catch { /* ignore */ }
      }, 5000);
      batchSucPollRef.current = poll;
      // Stop polling after 15 min max
      setTimeout(() => { clearInterval(poll); batchSucPollRef.current = null; setBatchSucProcessing(false); }, 900000);
    } catch (error) {
      setBatchSucProcessing(false);
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 503 || detail.includes('Playwright')) {
        toast.error('Automatización no disponible en este entorno.');
      } else {
        toast.error(detail || 'Error al procesar sucursales');
      }
    }
  };

  // Cancela el batch de sucursales y siempre devuelve el control al usuario (cierra el modal)
  const handleCancelarBatchSucursales = async () => {
    try {
      if (batchSucBatchId) {
        await api.post(`/waya/batch-sucursales/${batchSucBatchId}/cancelar`);
      }
    } catch { /* ignore: igual cerramos */ }
    if (batchSucPollRef.current) { clearInterval(batchSucPollRef.current); batchSucPollRef.current = null; }
    setBatchSucProcessing(false);
    setShowBatchSucModal(false);
    toast.info('Proceso cancelado');
    loadEmpresa();
  };

  // Reintenta filas que quedaron en ERROR. `indices`=null → reintenta todas las fallidas.
  const handleReintentarBatchSucursales = async (indices = null) => {
    if (!batchSucBatchId) return;
    setBatchSucProcessing(true);
    try {
      const res = await api.post(`/waya/batch-sucursales/${batchSucBatchId}/reintentar`, { indices }, { skipAuthRedirect: true });
      toast.success(res.data.message || 'Reintentando...');
      if (batchSucPollRef.current) clearInterval(batchSucPollRef.current);
      const poll = setInterval(async () => {
        try {
          const pRes = await api.get(`/waya/batch-sucursales/${batchSucBatchId}/progreso`, { skipAuthRedirect: true });
          setBatchSucProgreso(pRes.data);
          if (pRes.data.estado === 'COMPLETADO' || pRes.data.estado === 'CANCELADO' || pRes.data.estado === 'ERROR') {
            clearInterval(poll);
            batchSucPollRef.current = null;
            setBatchSucProcessing(false);
            loadEmpresa();
            if (pRes.data.estado === 'CANCELADO') toast.info('Proceso cancelado');
            else toast.success(`Reintento completado: ${pRes.data.completadas} OK, ${pRes.data.errores} errores`);
          }
        } catch { /* ignore */ }
      }, 5000);
      batchSucPollRef.current = poll;
      setTimeout(() => { clearInterval(poll); batchSucPollRef.current = null; setBatchSucProcessing(false); }, 900000);
    } catch (error) {
      setBatchSucProcessing(false);
      toast.error(error.response?.data?.detail || 'Error al reintentar');
    }
  };

  // ============== Batch Medios de Pago ==============
  const initBatchMediosPago = () => {
    const sucursales = empresa.sucursales_waya || [];
    if (sucursales.length === 0) {
      toast.info('No hay sucursales disponibles');
      return;
    }
    const sel = {};
    sucursales.forEach(s => { sel[s.waya_sucursal_id] = true; });
    setBatchMPSeleccion(sel);
    setBatchMPBatchId(null);
    setBatchMPProgreso(null);
    setBatchMPProcessing(false);
    setShowBatchMPModal(true);
  };

  const handleBatchMediosPago = async () => {
    const selIds = Object.entries(batchMPSeleccion).filter(([, v]) => v).map(([id]) => id);
    if (selIds.length === 0) {
      toast.error('Seleccione al menos una sucursal');
      return;
    }
    setBatchMPProcessing(true);
    try {
      const sucs = selIds.map(sid => {
        const suc = (empresa.sucursales_waya || []).find(s => s.waya_sucursal_id === sid);
        const firstUser = suc?.usuarios?.[0] || suc?.cuentas_asociadas?.[0] || {};
        return {
          waya_sucursal_id: sid,
          sucursal_nombre: suc?.nombre || sid,
          email_cuenta: firstUser.email || '',
        };
      });
      const res = await api.post('/waya/configurar-medios-pago-batch', {
        empresa_id: empresa.id,
        sucursales: sucs,
      });
      const bId = res.data.batch_id;
      setBatchMPBatchId(bId);
      toast.success(`Configurando medios de pago para ${sucs.length} sucursales...`);
      const poll = setInterval(async () => {
        try {
          const pRes = await api.get(`/waya/batch-mediopago/${bId}/progreso`, { skipAuthRedirect: true });
          setBatchMPProgreso(pRes.data);
          if (pRes.data.estado === 'COMPLETADO') {
            clearInterval(poll);
            setBatchMPProcessing(false);
            toast.success(`Completado: ${pRes.data.completadas} OK, ${pRes.data.errores} errores`);
          }
        } catch { /* ignore */ }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setBatchMPProcessing(false); }, 900000);
    } catch (error) {
      setBatchMPProcessing(false);
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 503 || detail.includes('Playwright')) {
        toast.error('Automatización no disponible en este entorno.');
      } else {
        toast.error(detail || 'Error al configurar medios de pago');
      }
    }
  };

  const handleRefrescarEmpresaWaya = async () => {
    setWayaRefrescando(true);
    try {
      const res = await api.post(`/waya/refrescar-empresa/${empresa.id}`);
      if (res.data.job_id) {
        toast.info('Sincronizando sucursales desde Waya...');
        pollWayaJob(res.data.job_id, 'Refrescar sucursales');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 503 || detail.includes('Playwright')) {
        toast.error('Automatización no disponible en este entorno.');
      } else {
        toast.error(detail || 'Error al refrescar desde Waya');
      }
    } finally { setWayaRefrescando(false); }
  };

  const handleDesvincularCuenta = async (cuenta) => {
    const label = cuenta.razon_social || cuenta.email || cuenta.cuit;
    if (!window.confirm(`¿Desvincular la cuenta "${label}"?`)) return;
    
    try {
      const res = await api.delete(`/empresas/${empresaId}/cuentas/desvincular`, {
        data: { email: cuenta.email, cuit: cuenta.cuit }
      });
      setEmpresa(res.data);
      toast.success('Cuenta desvinculada');
    } catch (error) {
      toast.error('Error al desvincular cuenta');
    }
  };

  // ============== Comentarios ==============
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const res = await api.post(`/empresas/${empresaId}/comentarios`, {
        texto: newComment
      });
      setEmpresa(res.data);
      setNewComment('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    } finally {
      setAddingComment(false);
    }
  };

  // ============== Cuentas Waya ==============
  const loadCuentasHistorial = async () => {
    setLoadingCuentas(true);
    try {
      const res = await api.get(`/cuentas-juridicas/empresa/${empresaId}`);
      setCuentasHistorial(res.data.cuentas || []);
    } catch {
      // silently fail
    } finally {
      setLoadingCuentas(false);
    }
  };

  // Auto-refresh cuentas pendientes cada 10 segundos
  // NOTA: dep = hasPending (bool estable) en vez de cuentasHistorial (array de nueva referencia cada fetch)
  const hasPendingCuentas = cuentasHistorial.some(c => c.estado === 'PENDIENTE' || c.estado === 'PROCESANDO');
  useEffect(() => {
    if (!hasPendingCuentas) return;
    const interval = setInterval(() => {
      loadCuentasHistorial();
    }, 10000);
    return () => clearInterval(interval);
  }, [hasPendingCuentas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling de progreso para cuentas con progreso activo
  // NOTA: dep = lista de ids concatenados (string estable) para evitar re-crear el interval cada fetch
  const cuentasConProgresoIds = cuentasHistorial
    .filter(c => c.progreso && !c.progreso.completado)
    .map(c => c.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!cuentasConProgresoIds) return;
    const ids = cuentasConProgresoIds.split(',');

    const pollProgress = async () => {
      const updates = {};
      for (const cuentaId of ids) {
        try {
          const res = await api.get(`/cuentas-juridicas/${cuentaId}/progreso`, { skipAuthRedirect: true });
          updates[cuentaId] = res.data.progreso;
          if (res.data.progreso?.completado) {
            loadCuentasHistorial();
            loadEmpresa();
          }
        } catch { /* ignore */ }
      }
      setProgresoMap(prev => ({ ...prev, ...updates }));
    };
    pollProgress();
    const interval = setInterval(pollProgress, 3000);
    return () => clearInterval(interval);
  }, [cuentasConProgresoIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start tracking progress for a cuenta
  const startProgressTracking = (cuentaId) => {
    setProgresoMap(prev => ({
      ...prev,
      [cuentaId]: { paso: 'Iniciando...', paso_num: 0, total_pasos: 1, completado: false }
    }));
    // Poll this specific cuenta aggressively
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/cuentas-juridicas/${cuentaId}/progreso`, { skipAuthRedirect: true });
        const prog = res.data.progreso;
        setProgresoMap(prev => ({ ...prev, [cuentaId]: prog }));
        if (prog?.completado) {
          clearInterval(interval);
          loadCuentasHistorial();
          loadEmpresa();
        }
      } catch { /* ignore */ }
    }, 2000);
    // Stop after 5 minutes max
    setTimeout(() => clearInterval(interval), 300000);
  };

  const handleReprocesarCuentas = async () => {
    if (!window.confirm('¿Reprocesar todas las cuentas con error?')) return;
    try {
      const res = await api.post(`/cuentas-juridicas/empresa/${empresaId}/reprocesar`);
      toast.success(res.data.message || 'Reprocesamiento iniciado');
      loadCuentasHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reprocesar');
    }
  };

  const handleCancelarTodasPendientes = async () => {
    if (!window.confirm('¿Cancelar TODAS las cuentas pendientes? No se crearán en Waya.')) return;
    try {
      const res = await api.post(`/cuentas-juridicas/empresa/${empresaId}/cancelar`);
      toast.success(res.data.message || 'Cuentas canceladas');
      loadCuentasHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleCancelarCuentaIndividual = async (cuenta) => {
    if (!window.confirm(`¿Cancelar la cuenta "${cuenta.datos?.razon_social}"?`)) return;
    try {
      const payload = cuenta.id
        ? { cuenta_id: cuenta.id }
        : { empresa_id: empresaId, numero: cuenta.numero };
      await api.post('/cuentas-juridicas/cancelar-individual', payload);
      toast.success('Cuenta cancelada');
      loadCuentasHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleEliminarCuentaWaya = async (cuenta) => {
    if (!window.confirm(`¿Eliminar la cuenta "${cuenta.datos?.razon_social || cuenta.numero}"?\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/cuentas-juridicas/${cuenta.id}`);
      toast.success('Cuenta eliminada');
      loadCuentasHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleActivarCuenta = async (cuenta) => {
    if (!cuenta.id) {
      toast.error('Esta cuenta no tiene ID. Reprocésela primero.');
      return;
    }
    // Cargar config de límites
    let lims = { ...limitesCuenta };
    try {
      const res = await api.get('/cuentas-juridicas/config/limites');
      const { limit_types, key, updated_at, updated_by, aprobar_identidad: ai, ...config } = res.data;
      lims = { ...lims, ...config };
    } catch { /* use current */ }

    if (!window.confirm(`¿Activar la cuenta "${cuenta.datos?.razon_social}"?\nSe aprobará identidad y se configurarán los límites.`)) return;
    try {
      const res = await api.post('/cuentas-juridicas/activar', {
        cuenta_id: cuenta.id,
        limites: lims,
        aprobar_identidad: true,
      });
      toast.success(res.data.message || 'Activación iniciada');
      startProgressTracking(cuenta.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar');
    }
  };

  const handleModificarLimites = async (cuenta) => {
    if (!cuenta.id) {
      toast.error('Esta cuenta no tiene ID.');
      return;
    }
    setModLimitesCuenta(cuenta);
    // Pre-fill with current limits from datos_waya or config
    const dw = cuenta.datos_waya || {};
    let defaults = { ...limitesCuenta };
    try {
      const res = await api.get('/cuentas-juridicas/config/limites');
      const { limit_types, key, updated_at, updated_by, aprobar_identidad: ai, ...config } = res.data;
      defaults = { ...defaults, ...config };
    } catch { /* use current */ }
    setModLimites({
      limite_diario: dw.limite_diario || defaults.limite_diario || '',
      limite_mensual: dw.limite_mensual || defaults.limite_mensual || '',
      limite_alerta: dw.limite_alerta || defaults.limite_alerta || '',
      limite_bloqueo_retiro: dw.limite_bloqueo_retiro || defaults.limite_bloqueo_retiro || '',
      limite_bloqueo_cuenta: dw.limite_bloqueo_cuenta || defaults.limite_bloqueo_cuenta || '',
      limite_bloqueo_cvu: dw.limite_bloqueo_cvu || defaults.limite_bloqueo_cvu || '',
    });
    setShowModLimitesModal(true);
  };

  const handleSubmitModLimites = async () => {
    if (!modLimitesCuenta?.id) return;
    setModificandoLimites(true);
    try {
      const res = await api.post('/cuentas-juridicas/modificar-limites', {
        cuenta_id: modLimitesCuenta.id,
        limites: modLimites,
      });
      toast.success(res.data.message || 'Modificación de límites iniciada');
      setShowModLimitesModal(false);
      startProgressTracking(modLimitesCuenta.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al modificar límites');
    } finally {
      setModificandoLimites(false);
    }
  };

  // Abre el diálogo para retomar la activación de una cuenta trabada en PENDIENTE
  const openRetomarModal = async (cuenta) => {
    if (!cuenta?.id) {
      toast.error('Esta cuenta no tiene ID. Reprocésela primero.');
      return;
    }
    setRetomarCuenta(cuenta);
    setRetomarAprobar(cuenta.aprobar_identidad !== false);
    // Pre-fill: límites guardados de la cuenta > config global > defaults locales
    const stored = cuenta.limites || {};
    let defaults = { ...limitesCuenta };
    try {
      const res = await api.get('/cuentas-juridicas/config/limites');
      const { limit_types, key, updated_at, updated_by, aprobar_identidad: ai, ...config } = res.data;
      defaults = { ...defaults, ...config };
    } catch { /* use current */ }
    setRetomarLimites({
      limite_diario: stored.limite_diario || defaults.limite_diario || '',
      limite_mensual: stored.limite_mensual || defaults.limite_mensual || '',
      limite_alerta: stored.limite_alerta || defaults.limite_alerta || '',
      limite_bloqueo_retiro: stored.limite_bloqueo_retiro || defaults.limite_bloqueo_retiro || '',
      limite_bloqueo_cuenta: stored.limite_bloqueo_cuenta || defaults.limite_bloqueo_cuenta || '',
      limite_bloqueo_cvu: stored.limite_bloqueo_cvu || defaults.limite_bloqueo_cvu || '',
    });
    setShowRetomarModal(true);
  };

  const handleRetomarActivacion = async () => {
    if (!retomarCuenta?.id) return;
    setRetomando(true);
    try {
      const res = await api.post('/cuentas-juridicas/activar', {
        cuenta_id: retomarCuenta.id,
        limites: retomarLimites,
        aprobar_identidad: retomarAprobar,
      });
      toast.success(res.data.message || 'Retomando activación...');
      setShowRetomarModal(false);
      startProgressTracking(retomarCuenta.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al retomar la activación');
    } finally {
      setRetomando(false);
    }
  };

  // Abre el diálogo "Continuar proceso" para una cuenta sincronizada/trabada (sin necesidad de h.id)
  const openContinuarModal = async (cu) => {
    if (!cu?.email) { toast.error('La cuenta no tiene email'); return; }
    let defaults = {
      limite_diario: '', limite_mensual: '', limite_alerta: '',
      limite_bloqueo_retiro: '', limite_bloqueo_cuenta: '', limite_bloqueo_cvu: '',
    };
    const stored = cu.historial?.limites || {};
    try {
      const res = await api.get('/cuentas-juridicas/config/limites');
      const { limit_types, key, updated_at, updated_by, aprobar_identidad: ai, ...config } = res.data;
      defaults = { ...defaults, ...config };
    } catch { /* use defaults */ }
    const limites = {};
    Object.keys(defaults).forEach(k => { limites[k] = stored[k] || defaults[k] || ''; });
    setContinuarModal({
      cu,
      limites,
      aprobar: cu.identidad !== 'Aprobada',
      crearSucursal: !cu.waya_sucursal_id,
    });
  };

  const handleContinuarProceso = async () => {
    if (!continuarModal?.cu?.email) return;
    const email = continuarModal.cu.email;
    setContinuando(true);
    try {
      const res = await api.post('/cuentas-juridicas/continuar', {
        empresa_id: empresaId,
        email,
        limites: continuarModal.limites,
        aprobar_identidad: continuarModal.aprobar,
        crear_sucursal: continuarModal.crearSucursal,
      }, { skipAuthRedirect: true });
      toast.success(res.data.message || 'Continuando el proceso por etapas...');
      setContinuarModal(null);
      // Mostrar progreso inicial inmediatamente
      setContinuarProgresoMap(prev => ({ ...prev, [email]: { paso: 'Iniciando...', etapa: 'ACTIVANDO', completado: false } }));
      startContinuarTracking(email);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al continuar el proceso');
    } finally {
      setContinuando(false);
    }
  };

  // Polling del progreso por email mientras corre el orquestador (activación + sucursal por etapas)
  const startContinuarTracking = (email) => {
    if (continuarPollRef.current[email]) clearInterval(continuarPollRef.current[email]);
    let elapsed = 0;
    const iv = setInterval(async () => {
      elapsed += 3500;
      try {
        const r = await api.get(`/cuentas-juridicas/progreso-por-email`, {
          params: { empresa_id: empresaId, email }, skipAuthRedirect: true,
        });
        if (r.data?.progreso) {
          setContinuarProgresoMap(prev => ({ ...prev, [email]: r.data.progreso }));
          if (r.data.progreso.completado) {
            clearInterval(iv);
            delete continuarPollRef.current[email];
            await loadCuentasHistorial();
            await loadEmpresa();
            toast.success('Proceso completado');
            // limpiar el progreso de la UI a los pocos segundos
            setTimeout(() => setContinuarProgresoMap(prev => { const n = { ...prev }; delete n[email]; return n; }), 8000);
          }
        }
      } catch { /* ignore */ }
      if (elapsed > 600000) { clearInterval(iv); delete continuarPollRef.current[email]; }
    }, 3500);
    continuarPollRef.current[email] = iv;
  };

  const handleSyncWaya = async (buscarPor) => {
    try {
      const res = await api.post('/cuentas-juridicas/sync-waya', {
        empresa_id: empresaId,
        buscar_por: buscarPor,
      });
      toast.success(res.data.message || 'Sincronización por CUIT iniciada');
      // Refresh after a delay (background task)
      setTimeout(() => { loadEmpresa(); loadCuentasHistorial(); }, 8000);
      setTimeout(() => { loadEmpresa(); loadCuentasHistorial(); }, 20000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar');
    }
  };

  const handleVincularExterna = async () => {
    if (!vincularEmail.trim()) {
      toast.error('Ingresá un email');
      return;
    }
    setVinculando(true);
    try {
      const res = await api.post('/cuentas-juridicas/vincular-externa', {
        empresa_id: empresaId,
        email: vincularEmail.trim(),
      });
      toast.success(res.data.message || 'Vinculación iniciada');
      setShowVincularModal(false);
      setVincularEmail('');
      // Refresh after background task completes
      setTimeout(() => { loadCuentasHistorial(); loadEmpresa(); }, 8000);
      setTimeout(() => { loadCuentasHistorial(); loadEmpresa(); }, 20000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular');
    } finally {
      setVinculando(false);
    }
  };

  const initCrearCuentas = async () => {
    // Cargar config de límites por defecto
    try {
      const res = await api.get('/cuentas-juridicas/config/limites');
      const { limit_types, key, updated_at, updated_by, ...config } = res.data;
      const { aprobar_identidad: ai, ...lims } = config;
      setLimitesCuenta(prev => ({ ...prev, ...lims }));
      setAprobarIdentidad(ai !== undefined ? ai : true);
    } catch { /* use defaults */ }

    setDatosCompartidos({
      cuit: empresa?.cuit || '',
      actividad: empresa?.actividad_principal || '',
      inicio_actividad: empresa?.inicio_actividad || '',
      celular: empresa?.telefono || '',
      pep_uif: 'NO',
      password: '',
      pin: ''
    });
    const razonBase = empresa?.razon_social || '';
    const emailBase = empresa?.email || '';
    const [user, domain] = emailBase.includes('@') ? emailBase.split('@') : [emailBase, ''];
    setCuentasIndividuales([{
      razon_social: razonBase ? `${razonBase} 1` : 'Cuenta 1',
      email: domain ? `${user}1@${domain}` : ''
    }]);
    setNumCuentas(1);
    setMostrarPassword(false);
    setShowCrearCuentasModal(true);
  };

  const handleNumCuentasChange = (val) => {
    const n = Math.max(1, Math.min(20, parseInt(val) || 1));
    setNumCuentas(n);
    const razonBase = empresa?.razon_social || '';
    const emailBase = empresa?.email || '';
    const nuevas = [];
    for (let i = 1; i <= n; i++) {
      const existente = cuentasIndividuales[i - 1];
      let razon = existente?.razon_social;
      let email = existente?.email;
      if (!razon) razon = razonBase ? `${razonBase} ${i}` : `Cuenta ${i}`;
      if (!email) {
        if (emailBase && emailBase.includes('@')) {
          const [u, d] = emailBase.split('@');
          email = `${u}${i}@${d}`;
        } else {
          email = '';
        }
      }
      nuevas.push({ razon_social: razon, email });
    }
    setCuentasIndividuales(nuevas);
  };

  const updateCuentaInd = (idx, field, value) => {
    setCuentasIndividuales(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const agregarCuentaInd = () => {
    const n = numCuentas + 1;
    setNumCuentas(n);
    const razonBase = empresa?.razon_social || '';
    const emailBase = empresa?.email || '';
    const [user, domain] = emailBase.includes('@') ? emailBase.split('@') : [emailBase, ''];
    setCuentasIndividuales(prev => [...prev, {
      razon_social: razonBase ? `${razonBase} ${n}` : `Cuenta ${n}`,
      email: domain ? `${user}${n}@${domain}` : ''
    }]);
  };

  const quitarCuentaInd = (idx) => {
    if (cuentasIndividuales.length <= 1) return;
    setCuentasIndividuales(prev => prev.filter((_, i) => i !== idx));
    setNumCuentas(prev => prev - 1);
  };

  const handleCrearCuentas = async () => {
    if (!datosCompartidos.cuit || !datosCompartidos.password || !datosCompartidos.pin) {
      toast.error('Complete CUIT, Contraseña y PIN');
      return;
    }
    if (cuentasIndividuales.some(c => !c.razon_social || !c.email)) {
      toast.error('Complete Razón Social y Email en todas las cuentas');
      return;
    }
    setCreandoCuentas(true);
    try {
      await api.post('/cuentas-juridicas/crear', {
        empresa_id: empresaId,
        datos_compartidos: datosCompartidos,
        cuentas: cuentasIndividuales,
        limites: limitesCuenta,
        aprobar_identidad: aprobarIdentidad,
      });
      toast.success(`${cuentasIndividuales.length} cuenta(s) enviadas a creación y activación`);
      setShowCrearCuentasModal(false);
      loadCuentasHistorial();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuentas');
    } finally {
      setCreandoCuentas(false);
    }
  };

  const handleDownloadAllDocs = async () => {
    try {
      const response = await api.get(`/empresas/${empresaId}/documentos/download-all`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documentos_${empresa?.razon_social?.replace(/\s+/g, '_') || 'empresa'}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Documentos descargados');
    } catch (error) {
      toast.error('Error al descargar documentos');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Empresa no encontrada</p>
      </div>
    );
  }

  const estadoStyle = getEstadoStyle(empresa.estado);
  const statusAnalysis = getStatusAnalysis();
  const StatusIcon = statusAnalysis.icon;

  // ============== Cuentas Unificadas (merge vinculadas + historial) ==============
  const cuentasUnificadas = (() => {
    const map = new Map();
    for (const cv of (empresa.cuentas_vinculadas || [])) {
      const key = (cv.email || '').toLowerCase().trim() || cv.cuit || `vinc_${Math.random()}`;
      map.set(key, {
        email: cv.email, razon_social: cv.razon_social, cuit: cv.cuit,
        cvu: cv.cvu, alias: cv.alias, identidad: cv.identidad,
        limite_diario: cv.limite_diario, limite_mensual: cv.limite_mensual,
        limite_alerta: cv.limite_alerta, limite_bloqueo_retiro: cv.limite_bloqueo_retiro,
        limite_bloqueo_cuenta: cv.limite_bloqueo_cuenta, limite_bloqueo_cvu: cv.limite_bloqueo_cvu,
        fecha_sync: cv.fecha_sync, waya_sucursal_id: cv.waya_sucursal_id,
        sucursal_nombre: cv.sucursal_nombre, vinculada: cv, historial: null,
      });
    }
    const validHistorial = cuentasHistorial.filter(c =>
      !['CANCELADA', 'ERROR', 'ELIMINADA'].includes(c.estado) && !c.error
    );
    const seenH = new Set();
    for (const ch of validHistorial) {
      const email = (ch.datos?.email || '').toLowerCase().trim();
      const key = email || ch.datos?.cuit || ch.id;
      if (seenH.has(key)) continue;
      seenH.add(key);
      if (map.has(key)) {
        const existing = map.get(key);
        existing.historial = ch;
        if (!existing.cvu && ch.datos_waya?.cvu) existing.cvu = ch.datos_waya.cvu;
        if (!existing.identidad && ch.datos_waya?.identidad) existing.identidad = ch.datos_waya.identidad;
      } else {
        const dw = ch.datos_waya || {};
        map.set(key, {
          email: ch.datos?.email, razon_social: ch.datos?.razon_social, cuit: ch.datos?.cuit,
          cvu: dw.cvu || null, alias: null, identidad: dw.identidad || null,
          limite_diario: dw.limite_diario, limite_mensual: dw.limite_mensual,
          limite_alerta: dw.limite_alerta, limite_bloqueo_retiro: dw.limite_bloqueo_retiro,
          limite_bloqueo_cuenta: dw.limite_bloqueo_cuenta, limite_bloqueo_cvu: dw.limite_bloqueo_cvu,
          fecha_sync: null, waya_sucursal_id: null, sucursal_nombre: null,
          vinculada: null, historial: ch,
        });
      }
    }
    return Array.from(map.values());
  })();

  return (
    <div className={embebido ? 'space-y-0' : 'p-6 lg:p-8 bg-slate-50 min-h-screen'} data-testid={embebido ? 'empresa-embebida' : undefined}>
      {/* Header */}
      <div className="mb-6">
        {!embebido && (
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft size={20} />
          <span>Volver a empresas</span>
        </button>
        )}
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="text-purple-600" size={32} />
              <h1 className="text-2xl font-bold text-slate-900">{empresa.razon_social}</h1>
              <Badge className={estadoStyle.color}>{estadoStyle.label}</Badge>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="font-mono">CUIT: {empresa.cuit}</span>
              {empresa.email && <span>📧 {empresa.email}</span>}
              {empresa.telefono && <span>📞 {empresa.telefono}</span>}
            </div>
            {empresa.domicilio && (
              <p className="text-sm text-slate-500 mt-1">📍 {empresa.domicilio}, {empresa.localidad}, {empresa.provincia}</p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {/* Botón Crear Cuentas Waya */}
            {['ACTIVA', 'APROBADA'].includes(empresa.estado) && (
              <button
                onClick={initCrearCuentas}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md text-white font-medium"
                style={{ backgroundColor: '#059669' }}
                data-testid="crear-cuentas-btn"
              >
                <PackageOpen size={16} />
                Crear Cuentas
              </button>
            )}
            <Select value={empresa.estado} onValueChange={(v) => openEstadoDialog(v)}>
              <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTADOS.map(e => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Cuadro de Resumen de Estado */}
      <Card className={`p-4 mb-6 border-l-4 ${
        statusAnalysis.color === 'green' ? 'border-l-green-500 bg-green-50' :
        statusAnalysis.color === 'red' ? 'border-l-red-500 bg-red-50' :
        statusAnalysis.color === 'blue' ? 'border-l-blue-500 bg-blue-50' :
        statusAnalysis.color === 'orange' ? 'border-l-orange-500 bg-orange-50' :
        statusAnalysis.color === 'yellow' ? 'border-l-yellow-500 bg-yellow-50' :
        'border-l-slate-500 bg-slate-50'
      }`}>
        <div className="flex items-center gap-4">
          {StatusIcon && (
            <div className={`p-3 rounded-full ${
              statusAnalysis.color === 'green' ? 'bg-green-100' :
              statusAnalysis.color === 'red' ? 'bg-red-100' :
              statusAnalysis.color === 'blue' ? 'bg-blue-100' :
              statusAnalysis.color === 'orange' ? 'bg-orange-100' :
              statusAnalysis.color === 'yellow' ? 'bg-yellow-100' :
              'bg-slate-100'
            }`}>
              <StatusIcon size={24} className={`${
                statusAnalysis.color === 'green' ? 'text-green-600' :
                statusAnalysis.color === 'red' ? 'text-red-600' :
                statusAnalysis.color === 'blue' ? 'text-blue-600' :
                statusAnalysis.color === 'orange' ? 'text-orange-600' :
                statusAnalysis.color === 'yellow' ? 'text-yellow-600' :
                'text-slate-600'
              }`} />
            </div>
          )}
          <div className="flex-1">
            <h3 className={`font-semibold text-lg ${
              statusAnalysis.color === 'green' ? 'text-green-800' :
              statusAnalysis.color === 'red' ? 'text-red-800' :
              statusAnalysis.color === 'blue' ? 'text-blue-800' :
              statusAnalysis.color === 'orange' ? 'text-orange-800' :
              statusAnalysis.color === 'yellow' ? 'text-yellow-800' :
              'text-slate-800'
            }`}>
              {statusAnalysis.message}
            </h3>
            {statusAnalysis.details && (
              <p className="text-sm mt-1 text-slate-600">
                Documentos faltantes: {statusAnalysis.details.join(', ')}
              </p>
            )}
            {statusAnalysis.reviewer && empresa.fecha_inicio_revision && (
              <p className="text-sm mt-1 text-blue-600">
                <UserCheck size={14} className="inline mr-1" />
                Desde: {new Date(empresa.fecha_inicio_revision).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Banner vinculación con Padrón */}
      {empresa.padron_data && (
        <Card className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200" data-testid="padron-link-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Link2 size={18} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-indigo-900">
                  Vinculada al Padrón como "{empresa.padron_data.razon_social_padron}"
                </p>
                <p className="text-xs text-indigo-600">
                  {empresa.cvus_unificados?.length || 0} CVUs registrados | 
                  {padronResumen ? ` ${padronResumen.alertas_total} alertas | ${padronResumen.movimientos_total} movimientos` : ' Cargando...'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
              onClick={() => navigate(`/m/clientes/cliente/${empresa.padron_data.cuit_limpio}`)}
              data-testid="btn-ver-padron"
            >
              <ExternalLink size={14} className="mr-2" />
              Ver en Padrón
            </Button>
          </div>
        </Card>
      )}

      {/* Waya Empresa Status */}
      <Card className={`p-4 ${empresa.waya_empresa_id ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'}`} data-testid="waya-empresa-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${empresa.waya_empresa_id ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <Building2 size={18} className={empresa.waya_empresa_id ? 'text-emerald-600' : 'text-amber-600'} />
            </div>
            <div>
              {empresa.waya_empresa_id ? (
                <>
                  <p className="text-sm font-medium text-emerald-900">
                    Empresa en Waya (ID: {empresa.waya_empresa_id})
                  </p>
                  <p className="text-xs text-emerald-600">
                    {empresa.waya_nombre_fantasia || empresa.razon_social} | Sucursales: {empresa.sucursales_waya?.length || 0}
                    {empresa.waya_last_sync && <span className="ml-2 text-slate-400">Último sync: {new Date(empresa.waya_last_sync).toLocaleString('es-AR')}</span>}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-amber-900">Empresa no vinculada a Waya</p>
                  <p className="text-xs text-amber-600">Debe crear o vincular la empresa en Waya</p>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {empresa.waya_empresa_id ? (
              <>
                <Button size="sm" variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  onClick={handleRefrescarEmpresaWaya}
                  disabled={wayaRefrescando}
                  data-testid="btn-refrescar-waya">
                  <RefreshCw size={14} className={`mr-1 ${wayaRefrescando ? 'animate-spin' : ''}`} />
                  {wayaRefrescando ? 'Sincronizando...' : 'Refrescar'}
                </Button>
                <a href={`https://administracion.waya.com.ar/Empresas/Edit/${empresa.waya_empresa_id}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-100 transition-colors">
                  <ExternalLink size={12} /> Ver en Waya
                </a>
              </>
            ) : (
              <Button size="sm" onClick={() => { setShowWayaModal(true); setWayaMode(''); setWayaNombre(empresa.razon_social || ''); setWayaId(''); }}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-crear-vincular-waya">
                <Plus size={14} className="mr-1" /> Crear / Vincular
              </Button>
            )}
          </div>
        </div>

        {/* Sucursales list */}
        {empresa.waya_empresa_id && empresa.sucursales_waya?.length > 0 && (
          <div className="mt-4 border-t border-emerald-200 pt-3" data-testid="sucursales-waya-list">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Sucursales ({empresa.sucursales_waya.length})</p>
              <Button variant="outline" size="sm" className="h-7 text-[10px] text-white bg-amber-600 border-amber-600 hover:bg-amber-700" onClick={initBatchMediosPago} data-testid="batch-mediopago-btn" title="Configurar medios de pago en lote">
                <Settings size={12} className="mr-1" /> Medios de Pago
              </Button>
            </div>
            <div className="space-y-2">
              {empresa.sucursales_waya.map((suc, idx) => (
                <div key={suc.waya_sucursal_id || idx} className="p-3 bg-white rounded-lg border border-emerald-100" data-testid={`sucursal-waya-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">#{suc.waya_sucursal_id}</span>
                      <span className="text-sm font-medium text-slate-800">{suc.nombre}</span>
                      {suc.codigo && <span className="text-xs text-slate-400">Cód: {suc.codigo}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {suc.rubro_nombre && (() => {
                        const rubroInfo = rubrosDisponibles?.find(r => r.id === suc.rubro_id);
                        const porcentaje = rubroInfo?.porcentaje;
                        const isDiff = empresa.rubro_default?.id && suc.rubro_id !== empresa.rubro_default?.id;
                        return (
                          <Badge className={`text-[10px] ${isDiff ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'bg-blue-50 text-blue-700'}`} title={`Comisión: ${suc.rubro_nombre}${porcentaje !== undefined ? ` (${porcentaje}%)` : ''}`}>
                            {suc.rubro_nombre}{porcentaje !== undefined ? ` ${porcentaje}%` : ''}
                          </Badge>
                        );
                      })()}
                      <Badge className={suc.estado === 'Publicada' || suc.estado === 'Habilitada' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                        {suc.estado}
                      </Badge>
                      {/* Botón individual para configurar medios de pago */}
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-amber-700 hover:bg-amber-50" title="Configurar medios de pago (QR + Saldo)"
                        onClick={async () => {
                          if (!window.confirm(`Configurar medios de pago para sucursal "${suc.nombre}" (#${suc.waya_sucursal_id})?`)) return;
                          try {
                            toast.info(`Configurando medios de pago para ${suc.nombre}...`);
                            const res = await api.post('/waya/configurar-medios-pago', {
                              empresa_id: empresa.id,
                              waya_sucursal_id: suc.waya_sucursal_id,
                              email_cuenta: suc.usuarios?.[0]?.email || '',
                              sucursal_nombre: suc.nombre,
                            });
                            if (res.data.job_id) {
                              pollWayaJob(res.data.job_id, `MP ${suc.nombre}`);
                            }
                          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                        }} data-testid={`config-mp-suc-${idx}`}>
                        <Settings size={10} className="mr-0.5" /> MP
                      </Button>
                      {/* Botón para asignar usuario (paso aislado) */}
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-blue-700 hover:bg-blue-50" title="Asignar usuario a sucursal (solo este paso)"
                          onClick={async () => {
                            const email = window.prompt('Email del usuario a asignar:', suc.usuarios?.[0]?.email || '');
                            if (!email) return;
                            try {
                              toast.info(`Asignando ${email} a la sucursal...`);
                              const res = await api.post('/waya/setup-sucursal', {
                                empresa_id: empresa.id,
                                email: email,
                                nombre_sucursal: suc.nombre,
                                codigo_sucursal: suc.codigo || '',
                                rubro_id: empresa.rubro_default?.id || '',
                                existing_sucursal_id: suc.waya_sucursal_id,
                                skip_create: true,
                                skip_medios_pago: true,
                              });
                              if (res.data.job_id) {
                                pollWayaJob(res.data.job_id, `Usuario ${suc.nombre}`);
                              }
                            } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                          }} data-testid={`assign-user-suc-${idx}`}>
                          <Plus size={10} className="mr-0.5" /> Usuario
                        </Button>
                      {/* Botón combinado: completar usuario + medios de pago (recupera sucursales en AVISO) */}
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-emerald-700 hover:bg-emerald-50" title="Completar usuario + medios de pago en una sola pasada (sin recrear la sucursal)"
                          onClick={async () => {
                            const defEmail = suc.usuarios?.[0]?.email || suc.cuentas_asociadas?.[0]?.email || '';
                            const email = window.prompt('Email del usuario a asignar (luego se configuran QR + Saldo):', defEmail);
                            if (!email) return;
                            try {
                              toast.info(`Completando usuario + medios de pago para ${suc.nombre}...`);
                              const res = await api.post('/waya/setup-sucursal', {
                                empresa_id: empresa.id,
                                email: email,
                                nombre_sucursal: suc.nombre,
                                codigo_sucursal: suc.codigo || '',
                                rubro_id: empresa.rubro_default?.id || '',
                                existing_sucursal_id: suc.waya_sucursal_id,
                                skip_create: true,
                                skip_assign_user: false,
                                skip_medios_pago: false,
                              });
                              if (res.data.job_id) {
                                pollWayaJob(res.data.job_id, `Usuario+MP ${suc.nombre}`);
                              }
                            } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                          }} data-testid={`complete-user-mp-suc-${idx}`}>
                          <CheckCircle size={10} className="mr-0.5" /> Usuario+MP
                        </Button>
                    </div>
                  </div>
                  {/* Usuarios de la sucursal */}
                  {suc.usuarios?.length > 0 && (
                    <div className="mt-2 pl-4 border-l-2 border-emerald-200">
                      {suc.usuarios.map((u, ui) => (
                        <div key={ui} className="flex items-center justify-between text-xs py-1">
                          <div className="flex items-center gap-2">
                            <User size={12} className="text-slate-400" />
                            <span className="text-slate-700">{u.nombre}</span>
                            <span className="text-slate-500">{u.email}</span>
                          </div>
                          {u.rol && <span className="text-slate-400">{u.rol}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Cuentas asociadas */}
                  {(() => {
                    let asociadas = suc.cuentas_asociadas || [];
                    // Fallback: if no cuentas_asociadas from backend, try matching by name
                    if (asociadas.length === 0 && empresa.cuentas_vinculadas) {
                      const sucName = (suc.nombre || '').trim().toUpperCase();
                      // Priority: exact match first, then contains
                      let match = empresa.cuentas_vinculadas.find(c => {
                        const cName = (c.razon_social || '').trim().toUpperCase();
                        return cName && sucName && cName === sucName;
                      });
                      if (!match) {
                        // Contains match - but prefer the longest match to avoid "RADDI SRL" matching "RADDI SRL 4"
                        const candidates = empresa.cuentas_vinculadas.filter(c => {
                          const cName = (c.razon_social || '').trim().toUpperCase();
                          return cName && sucName && (cName.includes(sucName) || sucName.includes(cName));
                        });
                        if (candidates.length > 0) {
                          // Pick the one with the most similar name length
                          match = candidates.reduce((best, c) => {
                            const bestDiff = Math.abs((best.razon_social || '').length - (suc.nombre || '').length);
                            const cDiff = Math.abs((c.razon_social || '').length - (suc.nombre || '').length);
                            return cDiff < bestDiff ? c : best;
                          });
                        }
                      }
                      if (match) asociadas = [{ razon_social: match.razon_social, email: match.email, cvu: match.cvu }];
                    }
                    return asociadas.length > 0 ? (
                      <div className="mt-2 pl-4 border-l-2 border-violet-200">
                        <p className="text-[10px] text-violet-500 uppercase font-medium mb-1">Cuentas asociadas</p>
                        {asociadas.map((ca, ci) => (
                          <div key={ci} className="flex items-center gap-2 text-xs py-0.5">
                            <CreditCard size={12} className="text-violet-400" />
                            <span className="font-medium text-violet-800">{ca.razon_social || ca.email}</span>
                            {ca.cvu && <span className="text-slate-400 font-mono text-[10px]">CVU: {ca.cvu}</span>}
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {suc.usuarios?.length > 0 && (!suc.cuentas_asociadas || suc.cuentas_asociadas.length === 0) && (
                    <p className="mt-1 text-[10px] text-amber-600 pl-4">Sin cuenta asociada (email no coincide con cuentas vinculadas)</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="documentacion" className="w-full">
            <TabsList className="bg-slate-100 border mb-4 p-1 w-full justify-start flex-wrap">
              <TabsTrigger value="documentacion" className="data-[state=active]:bg-white data-[state=active]:text-purple-700 data-[state=active]:shadow-sm text-slate-600 font-medium" data-testid="tab-documentacion">
                <FileText size={16} className="mr-2" />
                Documentación
              </TabsTrigger>
              <TabsTrigger value="cuentas" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm text-slate-600 font-medium" data-testid="tab-cuentas">
                <CreditCard size={16} className="mr-2" />
                Cuentas ({cuentasUnificadas.length + (empresa.cvus_unificados?.length || 0)})
              </TabsTrigger>
              {empresa.padron_data && (
                <>
                  <TabsTrigger value="alertas" className="data-[state=active]:bg-white data-[state=active]:text-amber-700 data-[state=active]:shadow-sm text-slate-600 font-medium" data-testid="tab-alertas">
                    <AlertTriangle size={16} className="mr-2" />
                    Alertas {padronResumen?.alertas_total > 0 && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] px-1.5">{padronResumen.alertas_total}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="movimientos" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm text-slate-600 font-medium" data-testid="tab-movimientos">
                    <Activity size={16} className="mr-2" />
                    Movimientos {padronResumen?.movimientos_total > 0 && <Badge className="ml-1 bg-blue-100 text-blue-700 text-[10px] px-1.5">{padronResumen.movimientos_total}</Badge>}
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="documentacion" className="space-y-6">
          {/* Documentos Requeridos con Estado */}
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="text-purple-600" size={20} />
                Documentación Requerida
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={async () => {
                    try {
                      const res = await api.post(`/empresas/${empresaId}/sync-documentos-onboarding`);
                      toast.success(res.data.message || 'Documentos sincronizados');
                      loadEmpresa();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'No se encontraron docs de onboarding');
                    }
                  }}
                  data-testid="sync-docs-onboarding-btn"
                >
                  <RefreshCw size={14} className="mr-1.5" />
                  Importar del Onboarding
                </Button>
                <button
                  onClick={() => {
                    setUploadingFiles([]);
                    setShowUploadDialog(true);
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md text-white"
                  style={{ backgroundColor: '#7c3aed' }}
                >
                  <Upload size={14} />
                  Subir
                </button>
              {empresa.documentos?.length > 0 && (
                <button
                  onClick={handleDownloadAllDocs}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="download-all-docs-btn"
                >
                  <Download size={14} />
                  Descargar Todo
                </button>
              )}
              </div>
            </div>
            
            <div className="space-y-2">
              {tiposDocumento.filter(t => t.obligatorio).map(tipo => {
                const doc = getDocumentoForTipo(tipo.codigo);
                const isMissing = !doc;
                const isRejected = doc?.estado === 'RECHAZADO';
                const isApproved = doc?.estado === 'APROBADO';
                const isPending = doc?.estado === 'PENDIENTE';
                const docDate = doc ? (doc.fecha_subida || doc.uploaded_at || doc.created_at || doc.fecha_aprobacion) : null;
                
                return (
                  <div 
                    key={tipo.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isMissing ? 'bg-red-50 border-red-200' :
                      isRejected ? 'bg-red-50 border-red-300' :
                      isApproved ? 'bg-green-50 border-green-200' :
                      isPending ? 'bg-yellow-50 border-yellow-200' :
                      'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {isMissing ? (
                        <XCircle size={20} className="text-red-500" />
                      ) : isRejected ? (
                        <XCircle size={20} className="text-red-600" />
                      ) : isApproved ? (
                        <CheckCircle size={20} className="text-green-600" />
                      ) : isPending ? (
                        <Clock size={20} className="text-yellow-600" />
                      ) : (
                        <RefreshCw size={20} className="text-blue-500" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium ${isMissing || isRejected ? 'text-red-700' : 'text-slate-800'}`}>
                          {tipo.nombre}
                        </span>
                        {doc && (
                          <p className="text-xs text-slate-500 truncate">
                            {doc.filename || doc.nombre} {docDate ? `- ${formatDocDate(docDate)}` : ''}
                          </p>
                        )}
                        {isRejected && doc.motivo_rechazo && (
                          <p className="text-xs text-red-600 mt-0.5">{doc.motivo_rechazo}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Badge className={`text-xs ${
                        isMissing ? 'bg-red-100 text-red-700' :
                        isRejected ? 'bg-red-100 text-red-700' :
                        isApproved ? 'bg-green-100 text-green-700' :
                        isPending ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {isMissing ? 'Faltante' :
                         isRejected ? 'Rechazado' :
                         isApproved ? 'Aprobado' :
                         isPending ? 'Pendiente' : 'En revisión'}
                      </Badge>
                      {doc && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setShowDocPreview(doc)} title="Ver documento">
                            <Eye size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={async () => {
                              try {
                                const response = await api.get(`/empresas/${empresaId}/documentos/${doc.id}/download`, { responseType: 'blob' });
                                const blobUrl = window.URL.createObjectURL(response.data);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = doc.filename || doc.nombre || 'documento';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(blobUrl);
                                document.body.removeChild(a);
                              } catch { toast.error('Error al descargar'); }
                            }} title="Descargar">
                            <Download size={14} />
                          </Button>
                          {isPending && (
                            <>
                              <button onClick={() => handleAprobarDocumento(doc.id, true)}
                                className="h-7 px-2 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200 hover:bg-green-200">
                                Aprobar
                              </button>
                              <button onClick={() => handleAprobarDocumento(doc.id, false)}
                                className="h-7 px-2 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200 hover:bg-red-200">
                                Rechazar
                              </button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteDocumento(doc.id)} title="Eliminar">
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Documentos Opcionales */}
            {tiposDocumento.filter(t => !t.obligatorio).length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-600 mb-2">Documentos Opcionales</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {tiposDocumento.filter(t => !t.obligatorio).map(tipo => {
                    const doc = getDocumentoForTipo(tipo.codigo);
                    
                    return (
                      <div key={tipo.id} className={`flex items-center justify-between p-2 rounded text-sm ${
                        doc ? (doc.estado === 'APROBADO' ? 'bg-green-50' : 'bg-yellow-50') : 'bg-slate-50'
                      }`}>
                        <div className="flex items-center gap-2">
                          {doc ? (
                            doc.estado === 'APROBADO' ? (
                              <CheckCircle size={14} className="text-green-600" />
                            ) : (
                              <Clock size={14} className="text-yellow-600" />
                            )
                          ) : (
                            <div className="w-3.5 h-3.5 rounded-full border border-slate-300" />
                          )}
                          <span className="text-slate-700">{tipo.nombre}</span>
                        </div>
                        {doc && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDocPreview(doc)}
                            className="h-6 w-6 p-0"
                          >
                            <Eye size={12} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>

          {/* Documentos sin tipo asignado (no matchean con el checklist) */}
          {(() => {
            const tiposCodigos = new Set(tiposDocumento.map(t => t.codigo?.toUpperCase()));
            const docsExcedentes = (empresa.documentos || []).filter(d => !tiposCodigos.has(d.tipo?.toUpperCase()));
            if (docsExcedentes.length === 0) return null;
            return (
              <Card className="p-6 bg-white">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FileText className="text-slate-500" size={20} />
                  Otros Documentos ({docsExcedentes.length})
                </h2>
                <div className="space-y-2">
                  {docsExcedentes.map(doc => {
                    const docEstado = DOC_ESTADOS[doc.estado] || DOC_ESTADOS.PENDIENTE;
                    const DocIcon = docEstado.icon;
                    const docDate = doc.fecha_subida || doc.uploaded_at || doc.created_at || doc.fecha_aprobacion;
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <DocIcon size={18} className={doc.estado === 'APROBADO' ? 'text-green-600' : doc.estado === 'RECHAZADO' ? 'text-red-600' : 'text-slate-400'} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 text-sm truncate">{doc.nombre || doc.filename}</p>
                            <p className="text-xs text-slate-500">
                              {doc.tipo || 'Sin tipo'} {docDate ? `- ${formatDocDate(docDate)}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Badge className={docEstado.color}>{docEstado.label}</Badge>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setShowDocPreview(doc)} title="Ver">
                            <Eye size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={async () => {
                              try {
                                const response = await api.get(`/empresas/${empresaId}/documentos/${doc.id}/download`, { responseType: 'blob' });
                                const blobUrl = window.URL.createObjectURL(response.data);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = doc.filename || doc.nombre || 'documento';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(blobUrl);
                                document.body.removeChild(a);
                              } catch { toast.error('Error al descargar'); }
                            }} title="Descargar">
                            <Download size={14} />
                          </Button>
                          {doc.estado === 'PENDIENTE' && (
                            <>
                              <button onClick={() => handleAprobarDocumento(doc.id, true)}
                                className="h-7 px-2 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">Aprobar</button>
                              <button onClick={() => handleAprobarDocumento(doc.id, false)}
                                className="h-7 px-2 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">Rechazar</button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                            onClick={() => handleDeleteDocumento(doc.id)} title="Eliminar">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })()}

          {/* Timeline de Eventos */}
          <Card className="p-6 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <History className="text-purple-600" size={20} />
              Timeline de Eventos
            </h2>
            
            <div className="relative">
              {/* Línea vertical */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
              
              <div className="space-y-4">
                {empresa.historial?.slice().reverse().map((evento, idx) => {
                  let iconColor = 'bg-slate-400';
                  let Icon = History;
                  
                  if (evento.tipo === 'CREACION') {
                    iconColor = 'bg-green-500';
                    Icon = Plus;
                  } else if (evento.tipo === 'DOCUMENTO') {
                    iconColor = 'bg-blue-500';
                    Icon = FileText;
                  } else if (evento.tipo === 'DOCUMENTO_REVISION') {
                    iconColor = evento.descripcion.includes('aprobado') ? 'bg-green-500' : 'bg-red-500';
                    Icon = evento.descripcion.includes('aprobado') ? CheckCircle : XCircle;
                  } else if (evento.tipo === 'CAMBIO_ESTADO') {
                    iconColor = 'bg-purple-500';
                    Icon = RefreshCw;
                  } else if (evento.tipo === 'REPRESENTANTE_LEGAL') {
                    iconColor = 'bg-cyan-500';
                    Icon = User;
                  } else if (evento.tipo === 'CVU_AGREGADO' || evento.tipo === 'CVU_DESACTIVADO') {
                    iconColor = 'bg-orange-500';
                    Icon = CreditCard;
                  } else if (evento.tipo === 'CUENTA_VINCULADA' || evento.tipo === 'CUENTA_DESVINCULADA') {
                    iconColor = 'bg-indigo-500';
                    Icon = Link2;
                  } else if (evento.tipo === 'COMENTARIO') {
                    iconColor = 'bg-pink-500';
                    Icon = MessageSquare;
                  }
                  
                  return (
                    <div key={evento.id || idx} className="relative flex gap-4 pl-10">
                      <div className={`absolute left-2 w-5 h-5 rounded-full ${iconColor} flex items-center justify-center`}>
                        <Icon size={12} className="text-white" />
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-800">{evento.descripcion}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {evento.usuario}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(evento.fecha).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
            </TabsContent>

            {/* TAB: CUENTAS */}
            <TabsContent value="cuentas" className="space-y-6">
              {/* Cuentas Unificadas */}
              <Card className="p-6 bg-white" data-testid="cuentas-unificadas-panel">
                <div className="flex justify-between items-center mb-5">
                  <div>
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <CreditCard className="text-emerald-600" size={20} />
                      Cuentas ({cuentasUnificadas.length})
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Cuentas vinculadas y creadas por automatizaci&oacute;n</p>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    {cuentasHistorial.some(c => c.estado === 'ERROR') && (
                      <Button variant="outline" size="sm" className="h-8 text-xs text-orange-600 border-orange-200 hover:bg-orange-50" onClick={handleReprocesarCuentas} data-testid="reprocesar-cuentas-btn">
                        <RefreshCw size={14} className="mr-1.5" /> Reprocesar
                      </Button>
                    )}
                    {cuentasHistorial.some(c => c.estado === 'PENDIENTE' || c.estado === 'PROCESANDO') && (
                      <>
                        <Badge className="bg-blue-100 text-blue-700 text-xs animate-pulse">Procesando...</Badge>
                        <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancelarTodasPendientes} data-testid="cancelar-todas-cuentas-btn">
                          <XCircle size={14} className="mr-1.5" /> Cancelar
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleSyncWaya('cuit')} title="Sincronizar cuentas por CUIT" data-testid="sync-waya-cuit-btn">
                      <RefreshCw size={14} className="mr-1.5" /> Sync CUIT
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowVincularModal(true)} title="Buscar cuenta por email" data-testid="sync-waya-email-btn">
                      <Search size={14} className="mr-1.5" /> Buscar Email
                    </Button>
                    {empresa.waya_empresa_id && (
                      <Button variant="outline" size="sm" className="h-8 text-xs text-teal-700 border-teal-300 hover:bg-teal-50" onClick={initBatchSucursales} data-testid="batch-sucursales-btn" title="Crear sucursales en lote">
                        <Building2 size={14} className="mr-1.5" /> Sucursales
                      </Button>
                    )}
                    {['ACTIVA', 'APROBADA'].includes(empresa.estado) && (
                      <Button variant="outline" size="sm" className="h-8 text-xs bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700" onClick={initCrearCuentas} data-testid="crear-cuentas-btn">
                        <Plus size={14} className="mr-1.5" /> Nueva Cuenta
                      </Button>
                    )}
                  </div>
                </div>

                {loadingCuentas ? (
                  <div className="text-center py-8">
                    <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
                    <p className="text-sm text-slate-400 mt-2">Cargando cuentas...</p>
                  </div>
                ) : cuentasUnificadas.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                    <CreditCard size={36} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-sm text-slate-500 font-medium">Sin cuentas</p>
                    <p className="text-xs text-slate-400 mt-1">Usa "Sync CUIT", "Buscar Email" o "Nueva Cuenta" para comenzar</p>
                    {['ACTIVA', 'APROBADA'].includes(empresa.estado) && (
                      <button className="mt-3 px-4 py-2 text-sm rounded-lg text-white font-medium" onClick={initCrearCuentas} style={{ backgroundColor: '#059669' }} data-testid="crear-cuentas-empty-btn">
                        Crear Cuentas
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {cuentasUnificadas.map((cu, idx) => {
                      const h = cu.historial;
                      const hasLimits = cu.limite_diario || cu.limite_mensual;
                      const isAprobada = cu.identidad === 'Aprobada';
                      const needsActivation = h && h.estado === 'CREADA' && h.resultado !== 'OK_ACTIVADA';
                      const isPending = h?.estado === 'PENDIENTE' || h?.estado === 'PROCESANDO';

                      // Determine status
                      let estadoLabel = 'SINCRONIZADA';
                      let estadoColor = 'bg-purple-100 text-purple-700 border-purple-200';
                      if (h) {
                        if (h.resultado === 'OK_ACTIVADA') { estadoLabel = 'ACTIVADA'; estadoColor = 'bg-green-100 text-green-700 border-green-200'; }
                        else if (h.resultado === 'OK_PARCIAL') { estadoLabel = 'PARCIAL'; estadoColor = 'bg-amber-100 text-amber-700 border-amber-200'; }
                        else if (h.resultado === 'VINCULADA_SIN_ACTIVAR') { estadoLabel = 'VINCULADA'; estadoColor = 'bg-blue-100 text-blue-700 border-blue-200'; }
                        else if (h.estado === 'PROCESANDO') { estadoLabel = 'PROCESANDO'; estadoColor = 'bg-blue-100 text-blue-700 border-blue-200'; }
                        else if (h.estado === 'PENDIENTE') { estadoLabel = 'PENDIENTE'; estadoColor = 'bg-yellow-100 text-yellow-700 border-yellow-200'; }
                        else if (h.estado === 'CREADA') { estadoLabel = 'CREADA'; estadoColor = 'bg-green-100 text-green-700 border-green-200'; }
                        else { estadoLabel = h.estado; estadoColor = 'bg-slate-100 text-slate-700 border-slate-200'; }
                      }

                      // Gradient header color
                      const headerGradient = isPending ? 'from-yellow-50 to-white' :
                        estadoLabel === 'ACTIVADA' ? 'from-emerald-50 to-white' :
                        cu.vinculada ? 'from-slate-50 to-white' : 'from-emerald-50 to-white';

                      return (
                        <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`cuenta-unificada-${idx}`}>
                          {/* Header */}
                          <div className={`flex items-center justify-between px-5 py-3 bg-gradient-to-r ${headerGradient} border-b border-slate-100`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${estadoLabel === 'ACTIVADA' ? 'bg-emerald-100' : isPending ? 'bg-yellow-100' : 'bg-purple-100'}`}>
                                <CreditCard size={16} className={estadoLabel === 'ACTIVADA' ? 'text-emerald-600' : isPending ? 'text-yellow-600' : 'text-purple-600'} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate">{cu.razon_social || 'Sin nombre'}</p>
                                {cu.email && <p className="text-xs text-slate-500 truncate">{cu.email}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                              {/* Sucursal badge / button */}
                              {cu.waya_sucursal_id ? (
                                <Badge className="bg-teal-50 text-teal-700 border-teal-200 text-[10px]" data-testid={`sucursal-badge-${idx}`}>
                                  Sucursal #{cu.waya_sucursal_id} {cu.sucursal_nombre ? `(${cu.sucursal_nombre})` : ''}
                                </Badge>
                              ) : empresa.waya_empresa_id && cu.vinculada ? (
                                <Button variant="ghost" size="sm" className="h-7 text-[10px] text-amber-600 hover:bg-amber-50 px-2"
                                  onClick={() => handleCrearSucursalCuenta(cu.vinculada)} data-testid={`btn-crear-sucursal-${idx}`} title="Crear sucursal">
                                  <Plus size={12} className="mr-0.5" /> Sucursal
                                </Button>
                              ) : null}
                              {/* Identidad badge */}
                              {cu.identidad && (
                                <Badge className={`text-[10px] ${isAprobada ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                                  {cu.identidad}
                                </Badge>
                              )}
                              {/* Pending actions */}
                              {isPending && h && (
                                <>
                                  <RefreshCw size={14} className="animate-spin text-yellow-600" />
                                  <Button variant="outline" size="sm" className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                                    onClick={() => openRetomarModal(h)} data-testid={`retomar-activacion-${idx}`} title="Retomar la activación (la cuenta ya fue creada en Waya)">
                                    <Zap size={12} className="mr-1" /> Retomar activación
                                  </Button>
                                  <button onClick={async () => {
                                    if (!window.confirm('Marcar esta cuenta como CREADA?')) return;
                                    try {
                                      await api.post('/cuentas-juridicas/forzar-completar', { empresa_id: empresa.id, cuenta_id: h.id, numero: h.numero });
                                      toast.success('Cuenta marcada como CREADA');
                                      loadCuentasHistorial();
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                                  }} className="text-green-500 hover:text-green-700 p-1" title="Forzar completar" data-testid={`forzar-completar-${idx}`}>
                                    <CheckCircle size={14} />
                                  </button>
                                  <button onClick={() => handleCancelarCuentaIndividual(h)} className="text-red-500 hover:text-red-700 p-1" title="Cancelar" data-testid={`cancelar-cuenta-${idx}`}>
                                    <XCircle size={14} />
                                  </button>
                                </>
                              )}
                              {/* Activate button */}
                              {needsActivation && (
                                <Button variant="outline" size="sm" className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                  onClick={() => handleActivarCuenta(h)} data-testid={`activar-cuenta-${idx}`}>
                                  <Zap size={12} className="mr-1" /> Activar
                                </Button>
                              )}
                              {/* Continuar proceso (resume completo por etapas) — cuentas incompletas (sincronizadas o sin sucursal) */}
                              {!isPending && (cu.identidad !== 'Aprobada' || !cu.waya_sucursal_id) && (
                                <Button variant="outline" size="sm" className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openContinuarModal(cu)} data-testid={`continuar-proceso-${idx}`}
                                  title="Continuar el proceso por etapas (activar identidad/límites + crear sucursal/usuario/MP). Resume cuentas trabadas por corte.">
                                  <Zap size={12} className="mr-1" /> Continuar proceso
                                </Button>
                              )}
                              {/* Modify limits button */}
                              {h && !needsActivation && !['PENDIENTE', 'ERROR', 'CANCELADA'].includes(h.estado) && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
                                  onClick={() => handleModificarLimites(h)} title="Modificar limites" data-testid={`modificar-limites-${idx}`}>
                                  <Settings size={14} />
                                </Button>
                              )}
                              {/* View client */}
                              {cu.cuit && (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                                  onClick={() => navigate(`/m/clientes/cliente/${cu.cuit}`)} title="Ver cliente">
                                  <Eye size={14} />
                                </Button>
                              )}
                              {/* Delete / unlink */}
                              {h ? (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                  onClick={() => handleEliminarCuentaWaya(h)} title="Eliminar cuenta" data-testid={`eliminar-cuenta-${idx}`}>
                                  <Trash2 size={14} />
                                </Button>
                              ) : cu.vinculada ? (
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                  onClick={() => handleDesvincularCuenta(cu.vinculada)} title="Desvincular">
                                  <Trash2 size={14} />
                                </Button>
                              ) : null}
                              {/* Status badge */}
                              <Badge className={`text-[10px] ${estadoColor}`}>
                                {estadoLabel}
                              </Badge>
                            </div>
                          </div>
                          {/* Body */}
                          <div className="px-5 py-4">
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                              {cu.cuit && (
                                <div>
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">CUIT</span>
                                  <span className="text-sm font-mono text-slate-700">{cu.cuit}</span>
                                </div>
                              )}
                              {cu.cvu && (
                                <div className="lg:col-span-2">
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">CVU</span>
                                  <span className="text-sm font-mono text-emerald-600 font-medium">{cu.cvu}</span>
                                </div>
                              )}
                              {cu.alias && (
                                <div className="col-span-2 lg:col-span-3">
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">Alias</span>
                                  <span className="text-sm font-mono text-slate-600">{cu.alias}</span>
                                </div>
                              )}
                            </div>

                            {/* Limits grid */}
                            {hasLimits && (
                              <div className="mt-4 pt-3 border-t border-slate-100">
                                <span className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 block">Limites configurados</span>
                                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                                  {[
                                    { label: 'Diario', value: cu.limite_diario, color: 'bg-blue-50 text-blue-700' },
                                    { label: 'Mensual', value: cu.limite_mensual, color: 'bg-indigo-50 text-indigo-700' },
                                    { label: 'Alerta', value: cu.limite_alerta, color: 'bg-amber-50 text-amber-700' },
                                    { label: 'Bloq. Retiro', value: cu.limite_bloqueo_retiro, color: 'bg-orange-50 text-orange-700' },
                                    { label: 'Bloq. Cuenta', value: cu.limite_bloqueo_cuenta, color: 'bg-red-50 text-red-700' },
                                    { label: 'Bloq. CVU', value: cu.limite_bloqueo_cvu, color: 'bg-rose-50 text-rose-700' },
                                  ].filter(l => l.value).map((lim) => (
                                    <div key={lim.label} className={`rounded-lg px-3 py-2 ${lim.color}`}>
                                      <span className="text-[10px] block opacity-70">{lim.label}</span>
                                      <span className="text-sm font-semibold">{lim.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Progress bar / Stepper por etapas (Activando → Sucursal → Usuario → MP) */}
                            {(() => {
                              const prog = continuarProgresoMap[cu.email] || (h ? (progresoMap[h.id] || h.progreso) : null);
                              if (!prog || (prog.completado && prog.timestamp && Date.now() - new Date(prog.timestamp).getTime() > 30000)) return null;
                              const isErr = prog.paso?.toLowerCase().includes('error') || prog.paso?.toLowerCase().includes('err:');
                              const curIdx = etapaIndex(prog);
                              const done = prog.completado || curIdx >= 4;
                              return (
                                <div className="p-3 rounded-lg border mt-3" style={{
                                  backgroundColor: done ? (isErr ? '#fef2f2' : '#f0fdf4') : '#eff6ff',
                                  borderColor: done ? (isErr ? '#fecaca' : '#bbf7d0') : '#bfdbfe',
                                }} data-testid={`progreso-cuenta-${h?.id || cu.email}`}>
                                  {/* Stepper de 4 etapas */}
                                  <div className="flex items-center mb-2">
                                    {ETAPAS_CONT.map((et, i) => {
                                      const stDone = done || curIdx > i;
                                      const stActive = !done && curIdx === i;
                                      const dotColor = stDone ? (isErr && stActive ? '#ef4444' : '#22c55e') : stActive ? '#3b82f6' : '#cbd5e1';
                                      return (
                                        <React.Fragment key={et.key}>
                                          <div className="flex flex-col items-center" style={{ minWidth: 56 }} data-testid={`etapa-${et.key}-${stDone ? 'done' : stActive ? 'active' : 'pending'}`}>
                                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: dotColor }}>
                                              {stDone ? <CheckCircle size={12} /> : stActive ? <RefreshCw size={11} className="animate-spin" /> : <span className="text-[9px]">{i + 1}</span>}
                                            </div>
                                            <span className="text-[9px] mt-1 text-center leading-tight" style={{ color: stActive ? '#2563eb' : stDone ? '#16a34a' : '#94a3b8' }}>{et.label}</span>
                                          </div>
                                          {i < ETAPAS_CONT.length - 1 && (
                                            <div className="flex-1 h-0.5 mb-3" style={{ backgroundColor: (done || curIdx > i) ? '#22c55e' : '#e2e8f0' }} />
                                          )}
                                        </React.Fragment>
                                      );
                                    })}
                                  </div>
                                  {/* Texto del paso actual */}
                                  <div className="flex items-center gap-2">
                                    {!done && <RefreshCw size={12} className="animate-spin text-blue-600" />}
                                    {done && !isErr && <CheckCircle size={12} className="text-green-600" />}
                                    {done && isErr && <AlertTriangle size={12} className="text-red-600" />}
                                    <span className="text-xs font-medium" style={{ color: done ? (isErr ? '#dc2626' : '#16a34a') : '#2563eb' }}>{prog.paso}</span>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Timestamps */}
                            <div className="flex gap-4 mt-3">
                              {cu.fecha_sync && (
                                <p className="text-[10px] text-slate-400">Sync: {new Date(cu.fecha_sync).toLocaleString('es-ES')}</p>
                              )}
                              {h?.created_at && (
                                <p className="text-[10px] text-slate-400">Creada: {new Date(h.created_at).toLocaleString('es-ES')} {h.creado_por_email ? `| ${h.creado_por_email}` : ''}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* CVUs unificados (empresa + padrón) */}
              {empresa.cvus_unificados?.length > 0 && (
                <CvusUnificadosTabla cvus={empresa.cvus_unificados} />
              )}
            </TabsContent>

            {/* ============== Tab Alertas ============== */}
            {empresa.padron_data && (
              <TabsContent value="alertas" className="space-y-6" data-testid="tab-alertas-content">
                <Card className="p-6 bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <AlertTriangle className="text-amber-600" size={20} />
                      Alertas
                    </h2>
                    <div className="flex gap-2">
                      {padronResumen && (
                        <>
                          <Badge className="bg-amber-50 text-amber-700">{padronResumen.alertas_total} total</Badge>
                          {padronResumen.alertas_abiertas > 0 && (
                            <Badge className="bg-red-50 text-red-700">{padronResumen.alertas_abiertas} abiertas</Badge>
                          )}
                          {padronResumen.alertas_criticas > 0 && (
                            <Badge className="bg-red-100 text-red-800">{padronResumen.alertas_criticas} críticas</Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {loadingPadron ? (
                    <div className="text-center py-8">
                      <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
                      <p className="text-sm text-slate-400 mt-2">Cargando alertas...</p>
                    </div>
                  ) : !padronResumen?.alertas?.length ? (
                    <div className="text-center py-8 text-slate-400">
                      <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
                      <p className="text-sm">No hay alertas para este CUIT</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {padronResumen.alertas.map((alerta, idx) => {
                        const sevColors = {
                          CRITICA: 'border-red-200 bg-red-50',
                          ALTA: 'border-orange-200 bg-orange-50',
                          MEDIA: 'border-yellow-200 bg-yellow-50',
                          BAJA: 'border-blue-200 bg-blue-50',
                          critical: 'border-red-200 bg-red-50',
                          high: 'border-orange-200 bg-orange-50',
                          medium: 'border-yellow-200 bg-yellow-50',
                          low: 'border-blue-200 bg-blue-50',
                        };
                        return (
                          <div key={alerta.id || idx} className={`p-3 rounded-lg border ${sevColors[alerta.severity] || 'border-slate-200 bg-slate-50'}`} data-testid={`alerta-${idx}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-800">{alerta.title || alerta.titulo}</p>
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{alerta.description || alerta.descripcion}</p>
                              </div>
                              <div className="flex flex-col items-end gap-1 ml-3">
                                <Badge className="text-[10px]">{alerta.severity || 'N/A'}</Badge>
                                <Badge variant="outline" className="text-[10px]">{alerta.status || alerta.estado}</Badge>
                              </div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2">{alerta.created_at ? formatDocDate(alerta.created_at, 'full') : ''}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
                {/* Casos relacionados */}
                {padronResumen?.casos?.length > 0 && (
                  <Card className="p-6 bg-white">
                    <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                      <FileText className="text-blue-600" size={20} />
                      Casos Relacionados ({padronResumen.casos.length})
                    </h2>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {padronResumen.casos.map((caso, idx) => (
                        <div key={caso.id || idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" data-testid={`caso-${idx}`}
                          onClick={() => navigate(`/m/investigaciones/caso/${caso.id}`)}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{caso.titulo}</p>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-1">{caso.descripcion}</p>
                            </div>
                            <div className="flex gap-1">
                              <Badge variant="outline" className="text-[10px]">{caso.estado}</Badge>
                              <Badge className="text-[10px]">{caso.prioridad}</Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </TabsContent>
            )}

            {/* ============== Tab Movimientos ============== */}
            {empresa.padron_data && (
              <TabsContent value="movimientos" className="space-y-6" data-testid="tab-movimientos-content">
                <Card className="p-6 bg-white">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Activity className="text-blue-600" size={20} />
                      Movimientos
                    </h2>
                    <div className="flex gap-2">
                      {padronResumen && (
                        <>
                          <Badge className="bg-blue-50 text-blue-700">{padronResumen.movimientos_total} total</Badge>
                          {padronResumen.monto_total > 0 && (
                            <Badge className="bg-green-50 text-green-700">
                              ${Number(padronResumen.monto_total).toLocaleString('es-AR')}
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {loadingPadron ? (
                    <div className="text-center py-8">
                      <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
                      <p className="text-sm text-slate-400 mt-2">Cargando movimientos...</p>
                    </div>
                  ) : !padronResumen?.movimientos?.length ? (
                    <div className="text-center py-8 text-slate-400">
                      <Activity size={32} className="mx-auto mb-2" />
                      <p className="text-sm">No hay movimientos registrados</p>
                      <p className="text-xs mt-1">Los movimientos aparecerán cuando se sincronicen desde Waya</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2 text-xs text-slate-500 font-medium">Fecha</th>
                            <th className="text-left p-2 text-xs text-slate-500 font-medium">Tipo</th>
                            <th className="text-left p-2 text-xs text-slate-500 font-medium">Descripción</th>
                            <th className="text-right p-2 text-xs text-slate-500 font-medium">Monto</th>
                            <th className="text-left p-2 text-xs text-slate-500 font-medium">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {padronResumen.movimientos.map((mov, idx) => (
                            <tr key={mov.id || idx} className="hover:bg-slate-50" data-testid={`movimiento-${idx}`}>
                              <td className="p-2 text-xs text-slate-600">{formatDocDate(mov.fecha || mov.created_at, 'full')}</td>
                              <td className="p-2"><Badge variant="outline" className="text-[10px]">{mov.tipo || '-'}</Badge></td>
                              <td className="p-2 text-xs text-slate-700 max-w-[200px] truncate">{mov.descripcion || mov.concepto || '-'}</td>
                              <td className="p-2 text-right text-xs font-semibold">
                                <span className={mov.monto >= 0 ? 'text-green-700' : 'text-red-700'}>
                                  ${Number(Math.abs(mov.monto || 0)).toLocaleString('es-AR')}
                                </span>
                              </td>
                              <td className="p-2"><Badge variant="outline" className="text-[10px]">{mov.estado || '-'}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
        <div className="space-y-6 overflow-y-auto max-h-fit">
          {/* Resumen Padrón */}
          {empresa.padron_data && (
            <Card className="p-5 bg-white border-indigo-100" data-testid="padron-resumen-sidebar">
              <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
                <BarChart3 className="text-indigo-600" size={18} />
                Resumen Padrón
              </h2>
              {loadingPadron ? (
                <div className="text-center py-3">
                  <RefreshCw size={16} className="animate-spin mx-auto text-slate-400" />
                </div>
              ) : padronResumen ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2.5 bg-amber-50 rounded-lg text-center">
                      <p className="text-[10px] text-amber-600 uppercase">Alertas</p>
                      <p className="text-lg font-bold text-amber-800">{padronResumen.alertas_total}</p>
                      {padronResumen.alertas_abiertas > 0 && (
                        <p className="text-[10px] text-red-600">{padronResumen.alertas_abiertas} abiertas</p>
                      )}
                    </div>
                    <div className="p-2.5 bg-blue-50 rounded-lg text-center">
                      <p className="text-[10px] text-blue-600 uppercase">Movimientos</p>
                      <p className="text-lg font-bold text-blue-800">{padronResumen.movimientos_total}</p>
                    </div>
                  </div>
                  {padronResumen.monto_total > 0 && (
                    <div className="p-2.5 bg-green-50 rounded-lg text-center">
                      <p className="text-[10px] text-green-600 uppercase">Monto Total</p>
                      <p className="text-base font-bold text-green-800">${Number(padronResumen.monto_total).toLocaleString('es-AR')}</p>
                    </div>
                  )}
                  <div className="p-2.5 bg-slate-50 rounded-lg text-center">
                    <p className="text-[10px] text-slate-500 uppercase">Casos</p>
                    <p className="text-lg font-bold text-slate-800">{padronResumen.casos_total}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50 mt-1"
                    onClick={() => navigate(`/m/clientes/cliente/${empresa.padron_data.cuit_limpio}`)}
                    data-testid="sidebar-ver-padron"
                  >
                    <ExternalLink size={12} className="mr-2" />
                    Ver detalle completo
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-400 text-center">Sin datos del padrón</p>
              )}
            </Card>
          )}
          {/* Inicio de Actividad */}
          <Card className="p-5 bg-white" data-testid="inicio-actividad-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Calendar className="text-teal-600" size={18} />
                Inicio de Actividad
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={empresa.inicio_actividad || ''}
                onChange={(e) => setEmpresa(prev => ({ ...prev, inicio_actividad: e.target.value }))}
                onBlur={async (e) => {
                  const val = e.target.value.trim();
                  try {
                    await api.patch(`/empresas/${empresa.id}`, { inicio_actividad: val });
                    toast.success('Inicio de actividad actualizado');
                  } catch { toast.error('Error al guardar'); }
                }}
                placeholder="DD/MM/AAAA"
                className="h-8 text-sm font-mono"
                style={{ backgroundColor: '#fff' }}
                data-testid="inicio-actividad-input"
              />
            </div>
            {empresa.inicio_actividad && (
              <p className="text-[10px] text-slate-400 mt-1">Se usa como valor por defecto al crear cuentas</p>
            )}
          </Card>

          {/* Representante Legal */}
          <Card className="p-6 bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <User className="text-purple-600" size={20} />
                Representante Legal
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRepData({ 
                    cuit: empresa.representante_legal?.cuit || '', 
                    es_excepcion: false, 
                    justificacion: '' 
                  });
                  setShowRepDialog(true);
                }}
              >
                <Edit2 size={16} />
              </Button>
            </div>
            
            {!empresa.representante_legal ? (
              <div className="text-center py-4">
                <User size={32} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-400">Sin asignar</p>
                <button
                  className="mt-3 px-3 py-1.5 text-sm rounded-md text-white"
                  onClick={() => setShowRepDialog(true)}
                  style={{ backgroundColor: '#7c3aed' }}
                >
                  Asignar Representante
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">CUIT</Label>
                  <p className="font-mono text-sm">{empresa.representante_legal.cuit}</p>
                </div>
                {empresa.representante_legal.nombre && (
                  <div>
                    <Label className="text-xs text-slate-500">Nombre</Label>
                    <p className="text-sm">{empresa.representante_legal.nombre}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Estado</Label>
                  <div className="mt-1">
                    {empresa.representante_legal.cuenta_verificada ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle size={12} className="mr-1" />
                        Cuenta Verificada
                      </Badge>
                    ) : empresa.representante_legal.es_excepcion ? (
                      <Badge className="bg-orange-100 text-orange-700">
                        <AlertTriangle size={12} className="mr-1" />
                        Excepción
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle size={12} className="mr-1" />
                        Pendiente
                      </Badge>
                    )}
                  </div>
                </div>
                {empresa.representante_legal.es_excepcion && empresa.representante_legal.justificacion_excepcion && (
                  <div>
                    <Label className="text-xs text-slate-500">Justificación</Label>
                    <p className="text-xs text-orange-700 mt-1">{empresa.representante_legal.justificacion_excepcion}</p>
                  </div>
                )}
                
                {/* Verificación AFIP */}
                <div className="pt-3 border-t mt-3">
                  <Label className="text-xs text-slate-500 mb-2 block">Verificación AFIP</Label>
                  <AFIPVerification 
                    cuit={empresa.representante_legal.cuit}
                    variant="card"
                    showDetails={false}
                  />
                </div>
              </div>
            )}
          </Card>

          {/* Alertas y Casos */}
          <AlertasCasosPanel empresaId={empresaId} />

          {/* Comisiones */}
          <ComisionPanel empresa={empresa} onUpdate={loadEmpresa} rubrosDisponibles={rubrosDisponibles} />
          
          {/* Estadísticas de Movimientos */}
          <EstadisticasPanel empresaId={empresaId} />

          {/* Comentarios */}
          <Card className="p-6 bg-white">
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <MessageSquare className="text-purple-600" size={20} />
              Comentarios
            </h2>
            
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {empresa.comentarios?.length === 0 || !empresa.comentarios ? (
                <p className="text-sm text-slate-400 text-center py-4">Sin comentarios</p>
              ) : (
                empresa.comentarios?.map((com, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-800">{com.texto}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {com.usuario} • {new Date(com.fecha).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2">
              <Input
                placeholder="Agregar comentario..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                style={{ backgroundColor: '#fff' }}
              />
              <button
                onClick={handleAddComment}
                disabled={addingComment || !newComment.trim()}
                className="px-3 py-2 rounded-md text-white disabled:opacity-50"
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Send size={16} />
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Dialog: Cambiar Estado */}
      <Dialog open={showEstadoDialog} onOpenChange={setShowEstadoDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>Cambiar Estado</DialogTitle>
            <DialogDescription>
              {nuevoEstado === 'EN_REVISION' 
                ? 'Asigne a un usuario para la revisión'
                : 'Complete los campos requeridos'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nuevo Estado</Label>
              <p className="font-medium text-lg">{ESTADOS.find(e => e.value === nuevoEstado)?.label}</p>
            </div>
            
            {nuevoEstado === 'EN_REVISION' && (
              <div>
                <Label>Asignar a *</Label>
                <Select value={asignadoA} onValueChange={setAsignadoA}>
                  <SelectTrigger style={{ backgroundColor: '#fff' }}>
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.email}>{u.nombre || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(nuevoEstado === 'RECHAZADA' || nuevoEstado === 'INFO_ADICIONAL') && (
              <div>
                <Label>Motivo *</Label>
                <Textarea
                  value={motivoEstado}
                  onChange={(e) => setMotivoEstado(e.target.value)}
                  placeholder="Ingrese el motivo..."
                  rows={3}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstadoDialog(false)}>Cancelar</Button>
            <button
              onClick={handleChangeEstado}
              disabled={
                (nuevoEstado === 'EN_REVISION' && !asignadoA) ||
                ((nuevoEstado === 'RECHAZADA' || nuevoEstado === 'INFO_ADICIONAL') && !motivoEstado.trim())
              }
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              Confirmar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Previsualización de Documento */}
      <Dialog open={!!showDocPreview} onOpenChange={() => setShowDocPreview(null)}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-purple-600" />
              {showDocPreview?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          {showDocPreview && (
            <div className="py-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Tipo de Documento</Label>
                  <p className="text-sm font-medium">{showDocPreview.tipo}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Estado</Label>
                  <Badge className={DOC_ESTADOS[showDocPreview.estado]?.color || 'bg-slate-100'}>
                    {DOC_ESTADOS[showDocPreview.estado]?.label || showDocPreview.estado}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Subido por</Label>
                  <p className="text-sm">{showDocPreview.subido_por || showDocPreview.uploaded_by || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Fecha de Subida</Label>
                  <p className="text-sm">{formatDocDate(showDocPreview.fecha_subida || showDocPreview.uploaded_at || showDocPreview.created_at || showDocPreview.fecha_aprobacion, 'full') || 'Sin fecha'}</p>
                </div>
              </div>
              
              {showDocPreview.comentario && (
                <div className="mb-4">
                  <Label className="text-xs text-slate-500">Comentario</Label>
                  <p className="text-sm p-2 bg-slate-50 rounded">{showDocPreview.comentario}</p>
                </div>
              )}
              
              {showDocPreview.motivo_rechazo && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg">
                  <Label className="text-xs text-red-600">Motivo de Rechazo</Label>
                  <p className="text-sm text-red-700">{showDocPreview.motivo_rechazo}</p>
                </div>
              )}
              
              {showDocPreview.aprobado_por && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg">
                  <Label className="text-xs text-green-600">Aprobado por</Label>
                  <p className="text-sm text-green-700">
                    {showDocPreview.aprobado_por} - {showDocPreview.fecha_aprobacion && formatDocDate(showDocPreview.fecha_aprobacion, 'full')}
                  </p>
                </div>
              )}
              
              <div className="flex items-center justify-center p-8 bg-slate-100 rounded-lg">
                <DocPreviewContent doc={showDocPreview} empresaId={empresaId} />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocPreview(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Subir Documentos */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="text-purple-600" />
              Subir Documentos
            </DialogTitle>
            <DialogDescription>
              Arrastre archivos o haga clic para seleccionar. Luego categorice cada documento.
            </DialogDescription>
          </DialogHeader>
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver ? 'border-purple-500 bg-purple-50' : 'border-slate-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload size={40} className="mx-auto mb-2 text-slate-400" />
            <p className="text-slate-600">Arrastre archivos aquí o</p>
            <label className="mt-2 inline-block">
              <span className="text-purple-600 cursor-pointer hover:underline">seleccione archivos</span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
            </label>
          </div>
          
          {uploadingFiles.length > 0 && (
            <div className="mt-4 space-y-3 max-h-60 overflow-y-auto">
              {uploadingFiles.map(file => (
                <div key={file.id} className={`p-3 rounded-lg ${file.uploaded ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm truncate max-w-xs">{file.nombre}</span>
                    <div className="flex items-center gap-2">
                      {file.uploading && <RefreshCw size={14} className="animate-spin text-purple-600" />}
                      {file.uploaded && <CheckCircle size={14} className="text-green-600" />}
                      {file.error && <XCircle size={14} className="text-red-600" />}
                      <button onClick={() => removeFileFromUpload(file.id)} className="text-slate-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  {!file.uploaded && (
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={file.tipo} onValueChange={(v) => updateFileData(file.id, 'tipo', v)}>
                        <SelectTrigger className="h-8 text-xs" style={{ backgroundColor: '#fff' }}>
                          <SelectValue placeholder="Tipo de documento" />
                        </SelectTrigger>
                        <SelectContent>
                          {tiposDocumento.map(t => (
                            <SelectItem key={t.id} value={t.codigo}>{t.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Comentario (opcional)"
                        value={file.comentario}
                        onChange={(e) => updateFileData(file.id, 'comentario', e.target.value)}
                        className="h-8 text-xs"
                        style={{ backgroundColor: '#fff' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={uploadDocuments}
              disabled={uploadingFiles.filter(f => !f.uploaded && f.tipo).length === 0}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              <FilePlus size={14} className="inline mr-1" />
              Subir Documentos
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Agregar CVU */}
      <Dialog open={showCvuDialog} onOpenChange={setShowCvuDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>Agregar CVU</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>CVU *</Label>
              <Input
                value={newCvu.cvu}
                onChange={(e) => setNewCvu({ ...newCvu, cvu: e.target.value })}
                placeholder="0000000000000000000000"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label>Alias</Label>
              <Input
                value={newCvu.alias}
                onChange={(e) => setNewCvu({ ...newCvu, alias: e.target.value })}
                placeholder="alias.empresa"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            <div>
              <Label>Banco</Label>
              <Input
                value={newCvu.banco}
                onChange={(e) => setNewCvu({ ...newCvu, banco: e.target.value })}
                placeholder="Nombre del banco"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCvuDialog(false)}>Cancelar</Button>
            <button
              onClick={handleAddCvu}
              className="px-4 py-2 rounded-md text-white"
              style={{ backgroundColor: '#7c3aed' }}
            >
              Agregar CVU
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Representante Legal */}
      <Dialog open={showRepDialog} onOpenChange={setShowRepDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>Representante Legal</DialogTitle>
            <DialogDescription>
              Ingrese el CUIT del representante legal. Se buscará en el padrón para verificar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>CUIT del Representante *</Label>
              <Input
                value={repData.cuit}
                onChange={(e) => setRepData({ ...repData, cuit: e.target.value })}
                placeholder="XX-XXXXXXXX-X"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="excepcion"
                checked={repData.es_excepcion}
                onChange={(e) => setRepData({ ...repData, es_excepcion: e.target.checked })}
              />
              <Label htmlFor="excepcion" className="cursor-pointer">
                Marcar como excepción (sin cuenta personal)
              </Label>
            </div>
            
            {repData.es_excepcion && (
              <div>
                <Label>Justificación *</Label>
                <Textarea
                  value={repData.justificacion}
                  onChange={(e) => setRepData({ ...repData, justificacion: e.target.value })}
                  placeholder="Explique la excepción..."
                  rows={3}
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepDialog(false)}>Cancelar</Button>
            <button
              onClick={handleSetRepresentante}
              disabled={searchingRep || !repData.cuit.trim() || (repData.es_excepcion && !repData.justificacion.trim())}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              {searchingRep ? 'Buscando...' : 'Establecer'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear Cuentas Waya (Full Page) */}
      <Dialog open={showCrearCuentasModal} onOpenChange={setShowCrearCuentasModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageOpen className="text-emerald-600" />
              Crear Cuentas Waya - {empresa?.razon_social}
            </DialogTitle>
            <DialogDescription>
              Complete los datos para crear cuentas jurídicas en el portal Waya.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Cantidad de cuentas */}
            <div className="flex items-center gap-3">
              <Label className="text-xs text-emerald-700 whitespace-nowrap">Cantidad de cuentas:</Label>
              <Input
                type="number" min="1" max="20"
                value={numCuentas}
                onChange={(e) => handleNumCuentasChange(e.target.value)}
                className="w-20 h-8 text-center"
                style={{ backgroundColor: '#fff' }}
                data-testid="empresa-num-cuentas-input"
              />
            </div>
            
            {/* Datos compartidos */}
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Datos compartidos (aplican a todas las cuentas)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">CUIT *</Label>
                  <Input value={datosCompartidos.cuit}
                    onChange={(e) => setDatosCompartidos(prev => ({ ...prev, cuit: e.target.value }))}
                    placeholder="20-12345678-9" style={{ backgroundColor: '#fff' }}
                    data-testid="empresa-cuenta-cuit" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Actividad *</Label>
                  <Input value={datosCompartidos.actividad}
                    onChange={(e) => setDatosCompartidos(prev => ({ ...prev, actividad: e.target.value }))}
                    placeholder="Ej: Servicios financieros" style={{ backgroundColor: '#fff' }}
                    data-testid="empresa-cuenta-actividad" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Inicio de Actividad *</Label>
                  <Input value={datosCompartidos.inicio_actividad}
                    onChange={(e) => setDatosCompartidos(prev => ({ ...prev, inicio_actividad: e.target.value }))}
                    placeholder="17/04/2026" style={{ backgroundColor: '#fff' }}
                    data-testid="empresa-cuenta-inicio" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Celular</Label>
                  <Input value={datosCompartidos.celular}
                    onChange={(e) => setDatosCompartidos(prev => ({ ...prev, celular: e.target.value }))}
                    placeholder="1122334455" style={{ backgroundColor: '#fff' }}
                    data-testid="empresa-cuenta-celular" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">PEP/UIF</Label>
                  <Select value={datosCompartidos.pep_uif}
                    onValueChange={(v) => setDatosCompartidos(prev => ({ ...prev, pep_uif: v }))}>
                    <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="empresa-cuenta-pep">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NO">NO</SelectItem>
                      <SelectItem value="SI">SI</SelectItem>
                      <SelectItem value="PEP">PEP</SelectItem>
                      <SelectItem value="UIF">UIF</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">PIN *</Label>
                  <Input value={datosCompartidos.pin}
                    onChange={(e) => setDatosCompartidos(prev => ({ ...prev, pin: e.target.value }))}
                    placeholder="123456" maxLength={6} style={{ backgroundColor: '#fff' }}
                    data-testid="empresa-cuenta-pin" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Contraseña *</Label>
                  <div className="relative">
                    <Input type={mostrarPassword ? "text" : "password"}
                      value={datosCompartidos.password}
                      onChange={(e) => setDatosCompartidos(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Contraseña" style={{ backgroundColor: '#fff' }}
                      data-testid="empresa-cuenta-password" />
                    <button type="button" onClick={() => setMostrarPassword(!mostrarPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {mostrarPassword ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Repetir Contraseña *</Label>
                  <Input type={mostrarPassword ? "text" : "password"}
                    value={datosCompartidos.password}
                    disabled className="bg-slate-50 text-slate-500"
                    data-testid="empresa-cuenta-password-repeat" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Se usa la misma contraseña</p>
                </div>
              </div>
            </div>
            
            {/* Cuentas individuales */}
            <div className="bg-white rounded-lg border border-emerald-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                  Cuentas individuales ({cuentasIndividuales.length})
                </p>
                <button type="button" onClick={agregarCuentaInd}
                  className="text-xs text-emerald-600 hover:text-emerald-800 font-medium flex items-center gap-1"
                  data-testid="empresa-agregar-cuenta-btn">
                  <Plus size={14} />Agregar cuenta
                </button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cuentasIndividuales.map((cuenta, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-500 w-6 text-center">{idx + 1}</span>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          value={cuenta.razon_social}
                          onChange={(e) => updateCuentaInd(idx, 'razon_social', e.target.value)}
                          placeholder="Razón Social"
                          className="h-8 text-sm"
                          style={{ backgroundColor: '#fff' }}
                          data-testid={`empresa-cuenta-razon-${idx}`}
                        />
                        <Input
                          value={cuenta.email}
                          onChange={(e) => updateCuentaInd(idx, 'email', e.target.value)}
                          placeholder="email@empresa.com"
                          className="h-8 text-sm"
                          style={{ backgroundColor: '#fff' }}
                          data-testid={`empresa-cuenta-email-${idx}`}
                        />
                      </div>
                      <button type="button" onClick={() => quitarCuentaInd(idx)}
                        disabled={cuentasIndividuales.length <= 1}
                        className={`p-1 rounded ${cuentasIndividuales.length <= 1 ? 'text-slate-300' : 'text-red-400 hover:text-red-600 hover:bg-red-50'}`}
                        data-testid={`empresa-quitar-cuenta-${idx}`}>
                        <X size={16} />
                      </button>
                    </div>
                    {/* Sucursal fields */}
                    {empresa?.waya_empresa_id && (
                      <div className="ml-8 grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-blue-600">Nombre Sucursal</Label>
                          <Input
                            value={cuenta.nombre_sucursal ?? (cuenta.razon_social || '')}
                            onChange={(e) => updateCuentaInd(idx, 'nombre_sucursal', e.target.value)}
                            placeholder="Nombre sucursal"
                            className="h-7 text-xs"
                            style={{ backgroundColor: '#fff' }}
                            data-testid={`empresa-cuenta-sucursal-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-blue-600">Rubro/Comisión</Label>
                          <select
                            value={cuenta.rubro_id ?? (empresa?.rubro_default?.id || '13')}
                            onChange={(e) => updateCuentaInd(idx, 'rubro_id', e.target.value)}
                            className="w-full h-7 text-xs border rounded px-2 bg-white"
                            data-testid={`empresa-cuenta-rubro-${idx}`}
                          >
                            {(rubrosDisponibles || []).map(r => (
                              <option key={r.id} value={r.id}>
                                {r.nombre} ({r.porcentaje}%) {r.id === empresa?.rubro_default?.id ? '(default)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-1 text-xs text-blue-600">
                            <input type="checkbox" checked={cuenta.crear_sucursal !== false}
                              onChange={(e) => updateCuentaInd(idx, 'crear_sucursal', e.target.checked)}
                              className="rounded border-slate-300" />
                            Crear sucursal
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Activación y Límites */}
            <div className="bg-white rounded-lg border border-blue-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
                Activación y Límites (post-creación)
              </p>
              <p className="text-[10px] text-slate-500">
                Después de crear la cuenta, se aprobará la identidad y se configurarán estos límites automáticamente.
              </p>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="modal_aprobar_identidad"
                  checked={aprobarIdentidad}
                  onChange={(e) => setAprobarIdentidad(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="modal-aprobar-identidad"
                />
                <label htmlFor="modal_aprobar_identidad" className="text-sm text-slate-700">
                  Aprobar validación de identidad
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['limite_diario', 'Lím. Diario'],
                  ['limite_mensual', 'Lím. Mensual'],
                  ['limite_alerta', 'Lím. Alerta'],
                  ['limite_bloqueo_retiro', 'Lím. Bloq. Retiro'],
                  ['limite_bloqueo_cuenta', 'Lím. Bloq. Cuenta'],
                  ['limite_bloqueo_cvu', 'Lím. Bloq. CVU'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[10px] text-slate-500">{label}</Label>
                    <Input
                      type="text"
                      value={limitesCuenta[key] || ''}
                      onChange={(e) => setLimitesCuenta(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder="0.00"
                      className="h-8 text-sm"
                      style={{ backgroundColor: '#fff' }}
                      data-testid={`modal-limite-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCrearCuentasModal(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleCrearCuentas}
              disabled={creandoCuentas || !datosCompartidos.cuit || !datosCompartidos.password || !datosCompartidos.pin || cuentasIndividuales.length === 0}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#059669' }}
              data-testid="empresa-crear-cuentas-submit"
            >
              {creandoCuentas ? <RefreshCw size={14} className="animate-spin" /> : <PackageOpen size={14} />}
              {creandoCuentas ? 'Creando...' : `Crear y Activar ${cuentasIndividuales.length} Cuenta(s)`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Modificar Límites */}
      <Dialog open={showModLimitesModal} onOpenChange={setShowModLimitesModal}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="text-blue-600" />
              Modificar Límites
            </DialogTitle>
            <DialogDescription>
              {modLimitesCuenta?.datos?.razon_social || 'Cuenta'} - Solo se modificarán los límites (sin re-aprobar identidad).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {[
              ['limite_diario', 'Límite Diario'],
              ['limite_mensual', 'Límite Mensual'],
              ['limite_alerta', 'Límite Alerta'],
              ['limite_bloqueo_retiro', 'Límite Bloqueo Retiro'],
              ['limite_bloqueo_cuenta', 'Límite Bloqueo Cuenta'],
              ['limite_bloqueo_cvu', 'Límite Bloqueo CVU'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <Label className="text-xs text-slate-600 w-36 shrink-0">{label}</Label>
                <Input
                  type="text"
                  value={modLimites[key] || ''}
                  onChange={(e) => setModLimites(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder="0.00"
                  className="h-8 text-sm"
                  style={{ backgroundColor: '#fff' }}
                  data-testid={`mod-limite-${key}`}
                />
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModLimitesModal(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleSubmitModLimites}
              disabled={modificandoLimites}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#2563eb' }}
              data-testid="submit-mod-limites-btn"
            >
              {modificandoLimites ? <RefreshCw size={14} className="animate-spin" /> : <Settings size={14} />}
              {modificandoLimites ? 'Modificando...' : 'Modificar Límites'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Retomar activación (cuenta trabada en PENDIENTE) */}
      <Dialog open={showRetomarModal} onOpenChange={(o) => { if (!retomando) setShowRetomarModal(o); }}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }} data-testid="retomar-activacion-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="text-amber-600" />
              Retomar activación
            </DialogTitle>
            <DialogDescription>
              {retomarCuenta?.datos?.razon_social || 'Cuenta'} — La cuenta ya fue creada en Waya pero la activación quedó trabada.
              Se buscará la cuenta por email, se aprobará la identidad y se setearán estos límites. Confirmá o editá los valores.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={retomarAprobar}
                onChange={(e) => setRetomarAprobar(e.target.checked)}
                className="rounded border-slate-300" data-testid="retomar-aprobar-identidad" />
              Aprobar identidad
            </label>
            {[
              ['limite_diario', 'Límite Diario'],
              ['limite_mensual', 'Límite Mensual'],
              ['limite_alerta', 'Límite Alerta'],
              ['limite_bloqueo_retiro', 'Límite Bloqueo Retiro'],
              ['limite_bloqueo_cuenta', 'Límite Bloqueo Cuenta'],
              ['limite_bloqueo_cvu', 'Límite Bloqueo CVU'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <Label className="text-xs text-slate-600 w-36 shrink-0">{label}</Label>
                <Input
                  type="text"
                  value={retomarLimites[key] || ''}
                  onChange={(e) => setRetomarLimites(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder="0.00"
                  className="h-8 text-sm"
                  style={{ backgroundColor: '#fff' }}
                  data-testid={`retomar-limite-${key}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetomarModal(false)} disabled={retomando} data-testid="retomar-cancel-btn">
              Cancelar
            </Button>
            <button
              onClick={handleRetomarActivacion}
              disabled={retomando}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#d97706' }}
              data-testid="retomar-confirm-btn"
            >
              {retomando ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
              {retomando ? 'Retomando...' : 'Retomar activación'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog: Continuar proceso (resume por etapas) */}
      <Dialog open={!!continuarModal} onOpenChange={(o) => { if (!o && !continuando) setContinuarModal(null); }}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }} data-testid="continuar-proceso-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="text-emerald-600" size={18} />
              Continuar proceso
            </DialogTitle>
            <DialogDescription>
              {continuarModal?.cu?.razon_social || 'Cuenta'} · {continuarModal?.cu?.email}
              <br />La cuenta ya existe en Waya. Se completará por <b>etapas</b> (cada una en su propia sesión, para evitar cortes por memoria): aprobar identidad + límites, y crear sucursal + usuario + medios de pago.
            </DialogDescription>
          </DialogHeader>
          {continuarModal && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={continuarModal.aprobar}
                  onChange={(e) => setContinuarModal({ ...continuarModal, aprobar: e.target.checked })}
                  data-testid="continuar-aprobar-identidad" />
                Aprobar identidad {continuarModal.cu?.identidad === 'Aprobada' ? '(ya aprobada)' : ''}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={continuarModal.crearSucursal}
                  onChange={(e) => setContinuarModal({ ...continuarModal, crearSucursal: e.target.checked })}
                  data-testid="continuar-crear-sucursal" />
                Crear sucursal + asignar usuario + medios de pago {continuarModal.cu?.waya_sucursal_id ? '(ya tiene sucursal)' : ''}
              </label>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mt-2">Límites</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(continuarModal.limites).map((key) => (
                  <div key={key}>
                    <label className="text-[11px] text-slate-500">{LIMIT_LABELS_MAP[key] || key}</label>
                    <input
                      className="w-full border border-slate-200 rounded-md px-2 py-1 text-sm"
                      value={continuarModal.limites[key] || ''}
                      onChange={(e) => setContinuarModal({ ...continuarModal, limites: { ...continuarModal.limites, [key]: e.target.value } })}
                      data-testid={`continuar-limite-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContinuarModal(null)} disabled={continuando} data-testid="continuar-cancel-btn">
              Cancelar
            </Button>
            <button
              onClick={handleContinuarProceso}
              disabled={continuando}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#059669' }}
              data-testid="continuar-confirm-btn"
            >
              {continuando ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
              {continuando ? 'Continuando...' : 'Continuar proceso'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog: Setup Sucursal por pasos (granular) */}
      <Dialog open={!!stepsModal} onOpenChange={(o) => { if (!o) setStepsModal(null); }}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }} data-testid="setup-steps-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="text-amber-600" size={18} />
              Configurar sucursal por pasos
            </DialogTitle>
            <DialogDescription>
              {stepsModal?.nombre} {stepsModal?.cuenta?.email ? `· ${stepsModal.cuenta.email}` : ''}
            </DialogDescription>
          </DialogHeader>
          {stepsModal && (
            <div className="space-y-3">
              {stepsModal.existingSuc ? (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-md p-2 text-emerald-800" data-testid="setup-steps-existing">
                  Ya existe la sucursal <b>{stepsModal.existingSuc.nombre}</b> (#{stepsModal.existingSuc.waya_sucursal_id}). No hace falta crearla de nuevo.
                </div>
              ) : (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800" data-testid="setup-steps-new">
                  No se encontró una sucursal existente con este nombre. Se creará una nueva.
                </div>
              )}
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Pasos a ejecutar</p>
              {[
                { key: 'crear', label: 'Crear sucursal y vincular', desc: 'Crea la sucursal en Waya (si no existe).', disabled: !!stepsModal.existingSuc },
                { key: 'usuario', label: 'Asignar usuario', desc: 'Asigna la cuenta como usuario de la sucursal.' },
                { key: 'medios', label: 'Configurar medios de pago', desc: 'Activa QR + Saldo en cuenta.' },
              ].map((step) => (
                <label key={step.key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer ${step.disabled ? 'opacity-50 cursor-not-allowed bg-slate-50 border-slate-200' : stepsModal.steps[step.key] ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!stepsModal.steps[step.key]}
                    disabled={step.disabled}
                    onChange={(e) => setStepsModal({ ...stepsModal, steps: { ...stepsModal.steps, [step.key]: e.target.checked } })}
                    data-testid={`setup-step-${step.key}`}
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{step.label}</p>
                    <p className="text-xs text-slate-500">{step.desc}{step.disabled ? ' (ya hecho)' : ''}</p>
                  </div>
                </label>
              ))}
              <p className="text-[11px] text-slate-400">
                Tip: destildá los pasos ya hechos para correr solo lo que falta.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStepsModal(null)} disabled={stepsRunning} data-testid="setup-steps-cancel">
              Cancelar
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleEjecutarPasos} disabled={stepsRunning} data-testid="setup-steps-run">
              {stepsRunning ? <><RefreshCw size={14} className="animate-spin mr-1" /> Ejecutando...</> : 'Ejecutar pasos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog: Vincular Cuenta Externa */}
      <Dialog open={showVincularModal} onOpenChange={setShowVincularModal}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="text-purple-600" size={18} />
              Vincular Cuenta Existente
            </DialogTitle>
            <DialogDescription>
              Ingresá el email de la cuenta en Waya. Se buscará en Gestión de Cuentas y se vinculará a esta empresa.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-sm text-slate-600">Email de la cuenta en Waya</Label>
            <Input
              type="email"
              value={vincularEmail}
              onChange={(e) => setVincularEmail(e.target.value)}
              placeholder="ejemplo@empresa.com"
              className="mt-1"
              style={{ backgroundColor: '#fff' }}
              data-testid="vincular-email-input"
              onKeyDown={(e) => e.key === 'Enter' && handleVincularExterna()}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVincularModal(false); setVincularEmail(''); }}>
              Cancelar
            </Button>
            <button
              onClick={handleVincularExterna}
              disabled={vinculando || !vincularEmail.trim()}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#7c3aed' }}
              data-testid="submit-vincular-btn"
            >
              {vinculando ? <RefreshCw size={14} className="animate-spin" /> : <Link2 size={14} />}
              {vinculando ? 'Buscando...' : 'Vincular'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear/Vincular Empresa Waya */}
      <Dialog open={showWayaModal} onOpenChange={setShowWayaModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={20} className="text-amber-600" />
              {wayaMode === 'crear' ? 'Crear Empresa en Waya' : wayaMode === 'vincular' ? 'Vincular Empresa de Waya' : 'Empresa en Waya'}
            </DialogTitle>
          </DialogHeader>
          
          {!wayaMode ? (
            <div className="grid grid-cols-2 gap-4 py-4">
              <button
                onClick={() => setWayaMode('crear')}
                className="p-6 border-2 border-dashed border-emerald-300 rounded-lg hover:bg-emerald-50 transition-colors text-center"
                data-testid="waya-mode-crear"
              >
                <Plus size={32} className="mx-auto mb-2 text-emerald-600" />
                <p className="font-semibold text-emerald-800">Crear</p>
                <p className="text-xs text-slate-500 mt-1">Crear nueva empresa en Waya</p>
              </button>
              <button
                onClick={() => setWayaMode('vincular')}
                className="p-6 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-center"
                data-testid="waya-mode-vincular"
              >
                <Link2 size={32} className="mx-auto mb-2 text-blue-600" />
                <p className="font-semibold text-blue-800">Vincular</p>
                <p className="text-xs text-slate-500 mt-1">Ya existe en Waya</p>
              </button>
            </div>
          ) : wayaMode === 'crear' ? (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm text-slate-600">Nombre en Waya</Label>
                <Input value={wayaNombre} onChange={(e) => setWayaNombre(e.target.value)}
                  placeholder="Nombre de la empresa en Waya" style={{ backgroundColor: '#fff' }} data-testid="waya-nombre-input" />
                <p className="text-xs text-slate-400 mt-1">CUIT: {empresa?.cuit}</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setWayaMode('')}>Volver</Button>
                <Button size="sm" onClick={handleCrearEmpresaWaya} disabled={wayaLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirmar-crear-waya">
                  {wayaLoading ? <><RefreshCw size={14} className="animate-spin mr-1" /> Creando...</> : <><Plus size={14} className="mr-1" /> Crear en Waya</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm text-slate-600">ID de Waya</Label>
                <Input value={wayaId} onChange={(e) => setWayaId(e.target.value)}
                  placeholder="Ej: 658" type="number" style={{ backgroundColor: '#fff' }} data-testid="waya-id-input" />
                <p className="text-xs text-slate-400 mt-1">
                  El número que aparece en la URL: administracion.waya.com.ar/Empresas/Edit/<strong>{wayaId || '___'}</strong>
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setWayaMode('')}>Volver</Button>
                <Button size="sm" onClick={handleVincularEmpresaWaya} disabled={wayaLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-confirmar-vincular-waya">
                  {wayaLoading ? <><RefreshCw size={14} className="animate-spin mr-1" /> Vinculando...</> : <><Link2 size={14} className="mr-1" /> Vincular</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Batch Sucursales */}
      <Dialog open={showBatchSucModal} onOpenChange={(v) => { if (!v && batchSucPollRef.current) { clearInterval(batchSucPollRef.current); batchSucPollRef.current = null; } if (!v) setBatchSucProcessing(false); setShowBatchSucModal(v); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="batch-sucursales-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={20} className="text-teal-600" />
              Crear Sucursales - {empresa?.razon_social}
            </DialogTitle>
          </DialogHeader>

          {(() => {
            const sinSucursal = (empresa.cuentas_vinculadas || []).filter(c => !c.waya_sucursal_id && c.email);
            const selectedCount = Object.values(batchSucSeleccion).filter(Boolean).length;
            const defaultRubroNombre = empresa.rubro_default?.nombre || 'Sin rubro';

            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <span className="text-sm text-teal-800">
                    <strong>{sinSucursal.length}</strong> cuentas sin sucursal &middot; <strong>{selectedCount}</strong> seleccionadas
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => { const sel = {}; sinSucursal.forEach(c => { sel[c.email] = true; }); setBatchSucSeleccion(sel); }}>
                      Todas
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => setBatchSucSeleccion({})}>
                      Ninguna
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  Cada sucursal se creará con configuración de medios de pago (QR + Saldo en cuenta).
                  Comisión por defecto: <strong>{defaultRubroNombre}</strong>
                </p>

                <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                  {sinSucursal.map((cuenta, idx) => {
                    const isSelected = !!batchSucSeleccion[cuenta.email];
                    const rubroActual = batchSucRubros[cuenta.email] || empresa.rubro_default?.id || '';
                    // Find progress for this account from batch
                    const batchItem = batchSucProgreso?.items?.find(i => i.email?.toLowerCase() === cuenta.email?.toLowerCase());

                    return (
                      <div key={idx} className={`border rounded-lg p-3 transition-all ${isSelected ? 'border-teal-300 bg-teal-50/30' : 'border-slate-200'} ${batchItem ? 'pb-4' : ''}`} data-testid={`batch-suc-item-${idx}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={isSelected}
                            onChange={(e) => setBatchSucSeleccion(prev => ({ ...prev, [cuenta.email]: e.target.checked }))}
                            className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                            disabled={batchSucProcessing} data-testid={`batch-suc-check-${idx}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{cuenta.razon_social || 'Sin nombre'}</p>
                            <p className="text-xs text-slate-500 truncate">{cuenta.email} {cuenta.cuit ? `| ${cuenta.cuit}` : ''}</p>
                          </div>
                          <select value={rubroActual}
                            onChange={(e) => setBatchSucRubros(prev => ({ ...prev, [cuenta.email]: e.target.value }))}
                            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white w-44"
                            disabled={batchSucProcessing} data-testid={`batch-suc-rubro-${idx}`}>
                            <option value="">Sin rubro</option>
                            {rubrosDisponibles.map((r) => (
                              <option key={r.id} value={r.id}>{r.nombre} ({r.porcentaje ?? r.valor ?? ''}%)</option>
                            ))}
                          </select>
                          {/* Status badge */}
                          {batchItem && (
                            <Badge className={`text-[10px] ${batchItem.estado === 'COMPLETADA' ? 'bg-green-100 text-green-700' : batchItem.estado === 'COMPLETADA_CON_AVISO' ? 'bg-amber-100 text-amber-700' : batchItem.estado === 'ERROR' ? 'bg-red-100 text-red-700' : batchItem.estado === 'PROCESANDO' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {batchItem.estado === 'COMPLETADA_CON_AVISO' ? 'CREADA (AVISO)' : batchItem.estado}
                            </Badge>
                          )}
                        </div>
                        {/* Progress bar */}
                        {batchItem && (
                          <div className="mt-2 ml-7">
                            <div className="flex items-center gap-2 mb-1">
                              {batchItem.estado === 'PROCESANDO' && <RefreshCw size={12} className="animate-spin text-blue-600" />}
                              {batchItem.estado === 'COMPLETADA' && <CheckCircle size={12} className="text-green-600" />}
                              {batchItem.estado === 'COMPLETADA_CON_AVISO' && <AlertTriangle size={12} className="text-amber-600" />}
                              {batchItem.estado === 'ERROR' && <AlertTriangle size={12} className="text-red-600" />}
                              <span className="text-[10px] text-slate-600">{batchItem.progreso}</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full transition-all duration-700" style={{
                                width: ['COMPLETADA', 'COMPLETADA_CON_AVISO', 'ERROR'].includes(batchItem.estado) ? '100%' : '50%',
                                backgroundColor: batchItem.estado === 'COMPLETADA' ? '#22c55e' : batchItem.estado === 'COMPLETADA_CON_AVISO' ? '#f59e0b' : batchItem.estado === 'ERROR' ? '#ef4444' : '#3b82f6',
                              }} />
                            </div>
                            {batchItem.estado === 'ERROR' && !batchSucProcessing && (
                              <Button variant="outline" size="sm" className="h-6 mt-2 text-[10px] text-red-700 border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  const realIdx = batchSucProgreso.items.findIndex(i => i.email?.toLowerCase() === cuenta.email?.toLowerCase());
                                  handleReintentarBatchSucursales(realIdx >= 0 ? [realIdx] : null);
                                }}
                                data-testid={`batch-suc-retry-${idx}`}>
                                <RefreshCw size={11} className="mr-1" /> Reintentar
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Batch summary progress */}
                {batchSucProgreso && batchSucProcessing && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                    <RefreshCw size={16} className="animate-spin text-blue-600" />
                    <div className="text-sm text-blue-800">
                      Procesando: <strong>{batchSucProgreso.completadas + batchSucProgreso.errores}</strong> de <strong>{batchSucProgreso.total}</strong>
                      {batchSucProgreso.errores > 0 && <span className="text-red-600 ml-2">({batchSucProgreso.errores} errores)</span>}
                    </div>
                  </div>
                )}
                {batchSucProgreso?.estado === 'COMPLETADO' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                    <CheckCircle size={16} className="text-green-600" />
                    <div className="text-sm text-green-800">
                      Completado: <strong>{batchSucProgreso.completadas}</strong> OK, <strong>{batchSucProgreso.errores}</strong> errores
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  {batchSucProcessing ? (
                    <Button variant="outline" size="sm" onClick={handleCancelarBatchSucursales}
                      className="text-red-600 border-red-300 hover:bg-red-50" data-testid="batch-suc-cancelar-btn">
                      <X size={14} className="mr-1.5" /> Cancelar proceso
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setShowBatchSucModal(false)} data-testid="batch-suc-cerrar-btn">
                      {batchSucProgreso?.estado === 'COMPLETADO' ? 'Cerrar' : 'Cancelar'}
                    </Button>
                  )}
                  {(!batchSucProgreso || (batchSucProgreso.estado !== 'COMPLETADO' && batchSucProgreso.estado !== 'CANCELADO')) && (
                    <Button size="sm" onClick={handleBatchSucursales} disabled={batchSucProcessing || selectedCount === 0}
                      className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="batch-suc-procesar-btn">
                      {batchSucProcessing ? <><RefreshCw size={14} className="animate-spin mr-1.5" /> Procesando...</> : <><Building2 size={14} className="mr-1.5" /> Crear {selectedCount} Sucursales</>}
                    </Button>
                  )}
                  {!batchSucProcessing && batchSucProgreso?.items?.some(i => i.estado === 'ERROR') && (
                    <Button size="sm" onClick={() => handleReintentarBatchSucursales(null)}
                      className="bg-red-600 hover:bg-red-700 text-white" data-testid="batch-suc-reintentar-fallidas-btn">
                      <RefreshCw size={14} className="mr-1.5" /> Reintentar fallidas ({batchSucProgreso.items.filter(i => i.estado === 'ERROR').length})
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Batch Medios de Pago */}
      <Dialog open={showBatchMPModal} onOpenChange={(v) => { if (!batchMPProcessing) setShowBatchMPModal(v); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="batch-mediopago-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={20} className="text-orange-600" />
              Configurar Medios de Pago - {empresa?.razon_social}
            </DialogTitle>
          </DialogHeader>

          {(() => {
            const sucursales = empresa.sucursales_waya || [];
            const selectedCount = Object.values(batchMPSeleccion).filter(Boolean).length;

            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-amber-100 border border-amber-300 rounded-lg p-3">
                  <span className="text-sm text-amber-900">
                    <strong>{sucursales.length}</strong> sucursales &middot; <strong>{selectedCount}</strong> seleccionadas
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => { const sel = {}; sucursales.forEach(s => { sel[s.waya_sucursal_id] = true; }); setBatchMPSeleccion(sel); }}>
                      Todas
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => setBatchMPSeleccion({})}>
                      Ninguna
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  Se configurará QR y Saldo en cuenta en cada sucursal seleccionada.
                </p>

                <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                  {sucursales.map((suc, idx) => {
                    const isSelected = !!batchMPSeleccion[suc.waya_sucursal_id];
                    const firstUser = suc.usuarios?.[0] || suc.cuentas_asociadas?.[0] || {};
                    const batchItem = batchMPProgreso?.items?.find(i => i.waya_sucursal_id === suc.waya_sucursal_id);

                    return (
                      <div key={idx} className={`border rounded-lg p-3 transition-all ${isSelected ? 'border-orange-300 bg-orange-50/30' : 'border-slate-200'}`} data-testid={`batch-mp-item-${idx}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={isSelected}
                            onChange={(e) => setBatchMPSeleccion(prev => ({ ...prev, [suc.waya_sucursal_id]: e.target.checked }))}
                            className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                            disabled={batchMPProcessing} data-testid={`batch-mp-check-${idx}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">#{suc.waya_sucursal_id}</span>
                              <span className="text-sm font-medium text-slate-800 truncate">{suc.nombre}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">
                              {firstUser.email || 'Sin usuario'} {suc.rubro_nombre ? `| ${suc.rubro_nombre}` : ''}
                            </p>
                          </div>
                          <Badge className={suc.estado === 'Publicada' || suc.estado === 'Habilitada' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-slate-100 text-slate-600 text-[10px]'}>
                            {suc.estado}
                          </Badge>
                          {batchItem && (
                            <Badge className={`text-[10px] ${batchItem.estado === 'COMPLETADA' ? 'bg-green-100 text-green-700' : batchItem.estado === 'ERROR' ? 'bg-red-100 text-red-700' : batchItem.estado === 'PROCESANDO' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {batchItem.estado}
                            </Badge>
                          )}
                        </div>
                        {batchItem && (
                          <div className="mt-2 ml-7">
                            <div className="flex items-center gap-2 mb-1">
                              {batchItem.estado === 'PROCESANDO' && <RefreshCw size={12} className="animate-spin text-blue-600" />}
                              {batchItem.estado === 'COMPLETADA' && <CheckCircle size={12} className="text-green-600" />}
                              {batchItem.estado === 'ERROR' && <AlertTriangle size={12} className="text-red-600" />}
                              <span className="text-[10px] text-slate-600">{batchItem.progreso}</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full transition-all duration-700" style={{
                                width: batchItem.estado === 'COMPLETADA' ? '100%' : batchItem.estado === 'ERROR' ? '100%' : '50%',
                                backgroundColor: batchItem.estado === 'COMPLETADA' ? '#22c55e' : batchItem.estado === 'ERROR' ? '#ef4444' : '#3b82f6',
                              }} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {batchMPProgreso && batchMPProcessing && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                    <RefreshCw size={16} className="animate-spin text-blue-600" />
                    <div className="text-sm text-blue-800">
                      Procesando: <strong>{batchMPProgreso.completadas + batchMPProgreso.errores}</strong> de <strong>{batchMPProgreso.total}</strong>
                      {batchMPProgreso.errores > 0 && <span className="text-red-600 ml-2">({batchMPProgreso.errores} errores)</span>}
                    </div>
                  </div>
                )}
                {batchMPProgreso?.estado === 'COMPLETADO' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                    <CheckCircle size={16} className="text-green-600" />
                    <div className="text-sm text-green-800">
                      Completado: <strong>{batchMPProgreso.completadas}</strong> OK, <strong>{batchMPProgreso.errores}</strong> errores
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" size="sm" onClick={() => setShowBatchMPModal(false)} disabled={batchMPProcessing}>
                    {batchMPProgreso?.estado === 'COMPLETADO' ? 'Cerrar' : 'Cancelar'}
                  </Button>
                  {(!batchMPProgreso || batchMPProgreso.estado !== 'COMPLETADO') && (
                    <Button size="sm" onClick={handleBatchMediosPago} disabled={batchMPProcessing || selectedCount === 0}
                      className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-80 disabled:bg-amber-600" data-testid="batch-mp-procesar-btn">
                      {batchMPProcessing ? <><RefreshCw size={14} className="animate-spin mr-1.5" /> Procesando...</> : <><Settings size={14} className="mr-1.5" /> Configurar {selectedCount} Sucursales</>}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpresaDetalle;
